// BiomeRenderer - Base class for all biome rendering (road, terrain, roadside objects)
import * as THREE from 'three';
import { SimplexNoise } from '../utils/SimplexNoise.js';
import { RoadsideSpawner } from './RoadsideSpawner.js';
import { BiomePalette } from './BiomePalette.js';

export class BiomeRenderer {
    constructor(scene, camera, worldTranslator, config = {}) {
        this.scene = scene;
        this.camera = camera;
        this.worldTranslator = worldTranslator;

        // Noise generator for terrain and road
        this.noise = new SimplexNoise();

        // Road configuration
        this.roadWidth = config.roadWidth || 20;
        this.chunkLength = config.chunkLength || 500;
        this.pathSegmentLength = config.pathSegmentLength || 300;  // Controls CatmullRom spline point spacing
        this.pathLookAhead = config.pathLookAhead || 80;
        this.roadSplineTension = config.roadSplineTension || 0.0;  // Zero tension = minimal spline overshoot

        // Road markings configuration (MUTCD compliant two-line system)
        this.dashLength = config.dashLength || 3;
        this.dashGapLength = config.dashGapLength || 9;
        this.lineWidth = config.lineWidth || 0.15;           // Width of each center line
        this.centerLineSpacing = config.centerLineSpacing || 0.4;  // Gap between the two parallel lines
        this.noPassingCurvatureThreshold = config.noPassingCurvatureThreshold || 0.0002;  // Very sensitive to curves
        this.noPassingSlopeThreshold = config.noPassingSlopeThreshold || 0.06;
        this.noPassingLookAhead = config.noPassingLookAhead || 12;  // Look ahead for upcoming curves/hills
        this.minZoneSegments = config.minZoneSegments || 20;  // Minimum segments before zone change (~250 units)
        this.roadSegmentsPerChunk = config.roadSegmentsPerChunk || 40;  // Samples per chunk (40 is plenty)

        // Road surface color configuration
        this.roadColorBase = config.roadColorBase || 0x333333;
        this.roadColorVariant = config.roadColorVariant || 0x666666;
        this.roadColorGradientScale = config.roadColorGradientScale || 0.0003;
        this.roadColorGradientStrength = config.roadColorGradientStrength || 1.0;

        // Road texture configuration (procedural noise for tarmac look)
        this.roadTextureEnabled = config.roadTextureEnabled !== undefined ? config.roadTextureEnabled : true;
        this.roadTextureStrength = config.roadTextureStrength || 0.12;
        this.roadTextureScaleCoarse = config.roadTextureScaleCoarse || 0.15;
        this.roadTextureCoarseStrength = config.roadTextureCoarseStrength || 0.5;
        this.roadTextureScaleFine = config.roadTextureScaleFine || 0.8;
        this.roadTextureFineStrength = config.roadTextureFineStrength || 0.5;

        // Create road shader material (shared across all chunks)
        this.roadMaterial = this.createRoadMaterial();

        // Terrain configuration
        this.terrainColor = config.terrainColor || 0x5d4e6d;
        this.terrainAmplitudeLarge = config.terrainAmplitudeLarge || 80;
        this.terrainAmplitudeMedium = config.terrainAmplitudeMedium || 40;
        this.terrainAmplitudeSmall = config.terrainAmplitudeSmall || 20;
        this.terrainAmplitudeFine = config.terrainAmplitudeFine || 5;
        this.terrainHeightMultiplier = config.terrainHeightMultiplier || 1.0;
        this.waterLevel = config.waterLevel || -10;

        // Terrain distance variation configuration
        this.terrainFlatZoneMin = config.terrainFlatZoneMin || 0.01;
        this.terrainFlatZoneMax = config.terrainFlatZoneMax || 0.1;
        this.terrainBlendRange = config.terrainBlendRange || 0.15;
        this.terrainDistanceNoiseScale = config.terrainDistanceNoiseScale || 0.002;

        // Terrain dimensions
        this.terrainWidth = config.terrainWidth || 800;
        this.terrainSegmentsOut = config.terrainSegmentsOut || 50;
        this.terrainSegmentsAlong = config.terrainSegmentsAlong || 30;

        // Roadside strip - fills variable gap between road edge and grid terrain
        this.roadsideStripWidth = config.roadsideStripWidth || 100;         // Wide enough to cover max curve gap
        this.roadsideStripWidthLeft = config.roadsideStripWidthLeft;        // Per-side override for left
        this.roadsideStripWidthRight = config.roadsideStripWidthRight;      // Per-side override for right
        this.roadsideStripSegments = config.roadsideStripSegments || 6;     // Enough for smooth blending
        this.roadsideStripOverlap = config.roadsideStripOverlap || 0.02;    // How much strip extends under road edge (prevents gaps)
        this.mountainOverlapIntoStrip = config.mountainOverlapIntoStrip || 5; // How much mountains overlap into strip (hides seams)

        // Terrain feature system configuration
        this.terrainFeaturesEnabled = config.terrainFeaturesEnabled !== undefined ? config.terrainFeaturesEnabled : true;
        this.terrainFeatureScale = config.terrainFeatureScale || 0.00008;
        this.terrainFeatureWeights = config.terrainFeatureWeights || {
            dunes: 1.0,
            peaks: 0.8,
            plateaus: 0.6,
            spires: 0.3,
            cliffs: 0.4,
            valleys: 0.5
        };
        this.terrainFeatureBlend = config.terrainFeatureBlend || 0.35;
        this.terrainLayerHeights = config.terrainLayerHeights || [0.3, 0.7, 1.0, 1.3];
        this.terrainLayerDistances = config.terrainLayerDistances || [0.02, 0.08, 0.25, 1.0];

        // ═══════════════════════════════════════════════════════════════════════════
        // PER-SIDE TERRAIN CONFIGURATION (asymmetric terrain support)
        // Each side can override base terrain settings for effects like ocean on one side
        // ═══════════════════════════════════════════════════════════════════════════
        this.terrainSides = {
            left: this._parseTerrainSideConfig(config.terrainLeft),
            right: this._parseTerrainSideConfig(config.terrainRight)
        };

        // ═══════════════════════════════════════════════════════════════════════════
        // ROAD GENERATION: Macro path + micro noise
        // Instead of independent events, we use:
        // 1. Slowly-changing target offsets that the road commits to
        // 2. Small random noise for character
        // ═══════════════════════════════════════════════════════════════════════════

        // Macro path settings - how the road's general direction changes
        this.roadMacroSegmentLengthMin = config.roadMacroSegmentLengthMin || 2000;  // Min distance between changes
        this.roadMacroSegmentLengthMax = config.roadMacroSegmentLengthMax || 8000;  // Max distance between changes
        this.roadMaxLateralOffset = config.roadMaxLateralOffset || 250;       // Max X drift from center
        this.roadMaxVerticalOffset = config.roadMaxVerticalOffset || 40;      // Max Y drift from base elevation

        // Road generation state
        this.eventSeed = config.eventSeed || Math.floor(Math.random() * 1000000);
        this.roadTotalZ = 0;

        // Normalize feature weights for easier use
        this._normalizeFeatureWeights();

        // Roadside object configuration
        this.enableRoadsideObjects = config.enableRoadsideObjects !== undefined ? config.enableRoadsideObjects : true;
        this.roadsideObjectSpawnDistance = config.roadsideObjectSpawnDistance || 700;
        this.roadsideObjectDensity = config.roadsideObjectDensity || 0.3;
        this.roadsideObjectMinSafeZone = config.roadsideObjectMinSafeZone || 10;
        // Small buffer zone - frame delay handles terrain initialization timing
        // This just ensures objects don't spawn right at Z=0 where camera starts
        this.roadsideObjectStartDelay = config.roadsideObjectStartDelay || 2; // chunks
        this.roadsideObjectDistributionWidth = config.roadsideObjectDistributionWidth || 200;
        this.rockProbability = config.rockProbability || 0.6;
        this.rockMinSize = config.rockMinSize || 2;
        this.rockMaxSize = config.rockMaxSize || 7;
        this.shrubMinSize = config.shrubMinSize || 3;
        this.shrubMaxSize = config.shrubMaxSize || 10;
        this.shrubSpriteCount = config.shrubSpriteCount || 3;
        this.shrubScaleVariation = config.shrubScaleVariation || 0.3;
        this.shrubStretchVariation = config.shrubStretchVariation || 0.3;
        this.roadsideObjectFadeInDuration = config.roadsideObjectFadeInDuration || 0.5;
        this.shrubSpritePaths = config.shrubSpritePaths || [
            'assets/sprites/shrub1.png',
            'assets/sprites/shrub2.png',
            'assets/sprites/shrub3.png',
            'assets/sprites/shrub4.png',
            'assets/sprites/shrub5.png'
        ];

        // Road path data
        this.pathPoints = [];
        this.roadPath = null;

        // Chunk storage
        this.roadChunks = [];
        this.terrainChunks = [];
        this.terrainMaterials = [];
        this.waterChunks = [];

        // Palette manager for biome colors (handles time-of-day color transitions)
        this.palette = new BiomePalette(config.biomeName || 'Desert');

        // Raycaster for finding terrain surface height
        this._terrainRaycaster = new THREE.Raycaster();
        this._rayOrigin = new THREE.Vector3();
        this._rayDirection = new THREE.Vector3(0, -1, 0); // Straight down
        this._terrainMeshCache = []; // Reusable array to avoid allocation per raycast

        // Dash pattern continuity tracking (for seamless dashes across chunks)
        this.dashPatternState = {
            cumulativeDistance: 0,  // Total road distance traveled
            lastChunkIndex: -1      // Last processed chunk index
        };

        // Marking zone state tracking (for consistent markings across chunks)
        this.markingZoneState = {
            lastZoneType: 'both_pass',  // Last zone type from previous chunk
            lastZoneLength: 0           // Accumulated length of current zone type
        };

        // Minimum zone length in world units (prevents short marking sections)
        this.minZoneLength = config.minZoneLength || 80;

        // Cache chunk boundary road data for seamless chunk connections
        // Key: rounded zEnd value, Value: roadData entry at that Z
        this.chunkBoundaryCache = new Map();

        // Cache terrain boundary vertices for seamless terrain chunk connections
        // Key: rounded Z + side (e.g., "-200_left"), Value: array of vertex heights at that boundary
        this.terrainBoundaryCache = new Map();

        // Road centerline cache - stores ALL sampled road positions for camera queries
        // Sorted array from highest Z (behind camera) to lowest Z (ahead)
        // Each entry: { z, position: Vector3, tangent: Vector3 }
        // This ensures camera uses exact positions that were used to render the road
        this.roadCenterlineCache = [];

        // Object pooling (optional - falls back to direct creation if not set)
        this.poolManager = null;
        this.biomeName = config.biomeName || 'Desert';

        // ═══════════════════════════════════════════════════════════════════════════
        // BIOME TRANSITION STATE - For smooth blending between biomes
        // ═══════════════════════════════════════════════════════════════════════════
        this.biomeTransition = {
            active: false,
            progress: 1.0,  // 0 = fully old biome, 1 = fully new biome
            chunksRemaining: 0,
            transitionChunks: 8,  // Number of chunks to blend over
            previous: null,  // Previous biome's terrain parameters
            startZ: 0,  // Z coordinate where transition started
            distanceZ: 4000  // Distance (in Z units) over which to blend terrain params
        };

        // Reusable Vector3 objects to reduce GC pressure (Phase 2 optimization)
        this._tempVec1 = new THREE.Vector3();
        this._tempVec2 = new THREE.Vector3();
        this._tempVec3 = new THREE.Vector3();
        this._tempVec4 = new THREE.Vector3();

        // Pre-allocated vectors for getRoadDataAtZ (called many times per frame)
        this._roadDataUp = new THREE.Vector3(0, 1, 0);
        this._roadDataRight = new THREE.Vector3();
        this._roadDataResult = { position: null, tangent: null, right: this._roadDataRight };

        // Create roadside spawner with callbacks to this BiomeRenderer
        // Note: We bind 'this' for methods that need BiomeRenderer context
        this.roadsideSpawner = new RoadsideSpawner({
            camera: this.camera,
            worldTranslator: this.worldTranslator,
            noise: this.noise,
            poolManager: null, // Will be set via setPoolManager()
            getRoadDataAtZ: (z) => this.getRoadDataAtZ(z),
            getTerrainHeightAtPoint: (x, z) => this.getTerrainHeightAtPoint(x, z),
            getColorsByCategory: (category) => this.getColorsByCategory(category)
        }, config);
    }

    async initialize(initialChunks = 100) {
        // Load shrub textures via spawner
        await this.roadsideSpawner.loadTextures();
        this.roadsideSpawner.initialize();

        // Track where we are in noise space (grows forever for unique terrain)
        this.totalNoiseDistance = 0;

        // Generate initial road path
        this.generateRoadPath();

        // Create initial chunks - each at increasingly negative Z
        for (let i = 0; i < initialChunks; i++) {
            const zStart = -i * this.chunkLength;
            const zEnd = zStart - this.chunkLength;

            // Extend road path if needed to cover this chunk plus lookahead
            // CatmullRom needs 4 control points; extend 3 extra segments for p3
            while (this.pathPoints.length > 0 &&
                   this.pathPoints[this.pathPoints.length - 1].z > zEnd - this.pathSegmentLength * 3) {
                this.extendRoadPath();
            }

            // Debug: log chunk 6 (around mile 10 = 3000 units)
            if (i === 6) {
                const lastPathZ = this.pathPoints[this.pathPoints.length - 1].z;
                console.log(`Chunk 6: zStart=${zStart}, zEnd=${zEnd}, pathPoints=${this.pathPoints.length}, lastPathZ=${lastPathZ}`);
            }

            this.createRoadChunk(i, zStart);
            this.createTerrainChunk(i, zStart);
        }

        // Track furthest Z we've generated to
        this.furthestGeneratedZ = -initialChunks * this.chunkLength;

        // Find lowest road point and ensure water is below it
        this.updateWaterLevelFromRoad();

        // Create water plane if water level is set
        this.createWaterPlane();

        // Enable roadside object spawning - now safe after all terrain is created
        this.roadsideSpawner.setReady();
    }

    /**
     * Set the pool manager for object pooling.
     * @param {PoolManager} poolManager - The pool manager instance
     */
    setPoolManager(poolManager) {
        this.poolManager = poolManager;
        // Also set pool manager on the roadside spawner
        if (this.roadsideSpawner) {
            this.roadsideSpawner.poolManager = poolManager;
        }
    }

    /**
     * Update biome configuration for seamless biome transitions.
     * New chunks will use the new config while existing chunks remain unchanged.
     * This allows smooth transitions without regenerating all terrain.
     *
     * @param {Object} config - New biome configuration
     * @param {Object} biomeColors - New biome color palettes (from biome class)
     */
    updateBiomeConfig(config, biomeColors = null) {
        // Save previous terrain parameters for blending
        this.biomeTransition.previous = {
            terrainColor: this.terrainColor,
            terrainAmplitudeLarge: this.terrainAmplitudeLarge,
            terrainAmplitudeMedium: this.terrainAmplitudeMedium,
            terrainAmplitudeSmall: this.terrainAmplitudeSmall,
            terrainAmplitudeFine: this.terrainAmplitudeFine,
            terrainHeightMultiplier: this.terrainHeightMultiplier,
            waterLevel: this.waterLevel,
            terrainFeatureWeights: { ...this.terrainFeatureWeights },
            terrainFeatureBlend: this.terrainFeatureBlend,
            terrainLayerHeights: [...this.terrainLayerHeights],
            terrainLayerDistances: [...this.terrainLayerDistances],
            // Road parameters - CRITICAL: these affect road position calculations
            roadMaxLateralOffset: this.roadMaxLateralOffset,
            roadMaxVerticalOffset: this.roadMaxVerticalOffset
        };

        // Start transition with per-Z interpolation for smooth height blending
        this.biomeTransition.active = true;
        this.biomeTransition.progress = 0;
        this.biomeTransition.chunksRemaining = this.biomeTransition.transitionChunks;
        // Store current Z position as transition start point
        this.biomeTransition.startZ = this.furthestGeneratedZ || 0;

        console.log(`[BiomeRenderer] Starting biome transition:`);
        console.log(`  startZ=${this.biomeTransition.startZ}, distanceZ=${this.biomeTransition.distanceZ}`);
        console.log(`  OLD amplitudeLarge=${this.biomeTransition.previous.terrainAmplitudeLarge}`);
        console.log(`  NEW amplitudeLarge=${config.terrainAmplitudeLarge || this.terrainAmplitudeLarge}`);

        // DO NOT clear terrain boundary cache - the first new biome chunk's first row
        // must match the last old biome chunk's last row (which is in the cache).
        // This ensures seamless vertex matching at the biome transition point.
        // Subsequent rows will use per-Z interpolation to smoothly blend to new params.

        // Update terrain configuration
        if (config.terrainColor !== undefined) this.terrainColor = config.terrainColor;
        if (config.terrainAmplitudeLarge !== undefined) this.terrainAmplitudeLarge = config.terrainAmplitudeLarge;
        if (config.terrainAmplitudeMedium !== undefined) this.terrainAmplitudeMedium = config.terrainAmplitudeMedium;
        if (config.terrainAmplitudeSmall !== undefined) this.terrainAmplitudeSmall = config.terrainAmplitudeSmall;
        if (config.terrainAmplitudeFine !== undefined) this.terrainAmplitudeFine = config.terrainAmplitudeFine;
        if (config.terrainHeightMultiplier !== undefined) this.terrainHeightMultiplier = config.terrainHeightMultiplier;
        if (config.waterLevel !== undefined) this.waterLevel = config.waterLevel;

        // Update terrain feature configuration
        if (config.terrainFeaturesEnabled !== undefined) this.terrainFeaturesEnabled = config.terrainFeaturesEnabled;
        if (config.terrainFeatureScale !== undefined) this.terrainFeatureScale = config.terrainFeatureScale;
        if (config.terrainFeatureWeights !== undefined) {
            this.terrainFeatureWeights = { ...this.terrainFeatureWeights, ...config.terrainFeatureWeights };
            this._normalizeFeatureWeights();
        }
        if (config.terrainFeatureBlend !== undefined) this.terrainFeatureBlend = config.terrainFeatureBlend;
        if (config.terrainLayerHeights !== undefined) this.terrainLayerHeights = config.terrainLayerHeights;
        if (config.terrainLayerDistances !== undefined) this.terrainLayerDistances = config.terrainLayerDistances;

        // Update per-side terrain configuration (asymmetric terrain)
        if (config.terrainLeft !== undefined) {
            this.terrainSides.left = this._parseTerrainSideConfig(config.terrainLeft);
        }
        if (config.terrainRight !== undefined) {
            this.terrainSides.right = this._parseTerrainSideConfig(config.terrainRight);
        }

        // Update road configuration
        if (config.roadWidth !== undefined) this.roadWidth = config.roadWidth;
        if (config.roadMaxLateralOffset !== undefined) this.roadMaxLateralOffset = config.roadMaxLateralOffset;
        if (config.roadMaxVerticalOffset !== undefined) this.roadMaxVerticalOffset = config.roadMaxVerticalOffset;

        // Update roadside object configuration
        if (config.enableRoadsideObjects !== undefined) this.enableRoadsideObjects = config.enableRoadsideObjects;
        if (config.roadsideObjectSpawnDistance !== undefined) this.roadsideObjectSpawnDistance = config.roadsideObjectSpawnDistance;
        if (config.roadsideObjectDensity !== undefined) this.roadsideObjectDensity = config.roadsideObjectDensity;
        if (config.roadsideObjectMinSafeZone !== undefined) this.roadsideObjectMinSafeZone = config.roadsideObjectMinSafeZone;
        if (config.roadsideObjectDistributionWidth !== undefined) this.roadsideObjectDistributionWidth = config.roadsideObjectDistributionWidth;

        // Update roadside spawner config if it exists
        if (this.roadsideSpawner && config.roadsideObjects) {
            this.roadsideSpawner.updateConfig(config.roadsideObjects);
        }

        // Update biome name
        if (config.biomeName !== undefined) {
            this.biomeName = config.biomeName;
            this.palette.biomeName = config.biomeName;
        }

        // Update color palettes if provided
        if (biomeColors) {
            this.biomeColors = biomeColors;
            this.palette.setBiomeColors(biomeColors);
            console.log(`[BiomeRenderer] Updated biomeColors for ${this.biomeName}, terrainColors[0]:`, biomeColors[0]?.terrainColors?.map(c => c.toString(16)));
        } else {
            console.log(`[BiomeRenderer] WARNING: No biomeColors provided for transition to ${this.biomeName}`);
        }

        console.log(`[BiomeRenderer] Updated config for seamless transition to ${this.biomeName}`);
    }

    /**
     * Get a blended terrain parameter value during biome transition.
     * Smoothly interpolates between previous and current biome values.
     *
     * @param {string} param - The parameter name to get
     * @returns {number} The blended value (or current value if no transition)
     */
    _getBlendedTerrainParam(param) {
        if (!this.biomeTransition.active || !this.biomeTransition.previous) {
            return this[param];
        }

        const prev = this.biomeTransition.previous[param];
        const curr = this[param];
        const t = this.biomeTransition.progress;

        // Smooth interpolation using ease-in-out
        const smoothT = t * t * (3 - 2 * t);

        return prev + (curr - prev) * smoothT;
    }

    /**
     * Get the blended terrain color during transition.
     * Interpolates RGB components separately for smooth color transition.
     *
     * @returns {number} The blended color hex value
     */
    _getBlendedTerrainColor() {
        if (!this.biomeTransition.active || !this.biomeTransition.previous) {
            return this.terrainColor;
        }

        const prevColor = this.biomeTransition.previous.terrainColor;
        const currColor = this.terrainColor;
        const t = this.biomeTransition.progress;

        // Smooth interpolation
        const smoothT = t * t * (3 - 2 * t);

        // Interpolate RGB components
        const prevR = (prevColor >> 16) & 0xff;
        const prevG = (prevColor >> 8) & 0xff;
        const prevB = prevColor & 0xff;

        const currR = (currColor >> 16) & 0xff;
        const currG = (currColor >> 8) & 0xff;
        const currB = currColor & 0xff;

        const r = Math.round(prevR + (currR - prevR) * smoothT);
        const g = Math.round(prevG + (currG - prevG) * smoothT);
        const b = Math.round(prevB + (currB - prevB) * smoothT);

        return (r << 16) | (g << 8) | b;
    }

    /**
     * Advance the biome transition progress after creating a chunk.
     * Called after each new terrain chunk is created.
     */
    _advanceBiomeTransition() {
        if (!this.biomeTransition.previous) return;

        // Check if we've traveled past the transition distance
        // Use Z-based check instead of chunk count for consistency with blending
        const startZ = this.biomeTransition.startZ;
        const distanceZ = this.biomeTransition.distanceZ;
        const currentZ = this.furthestGeneratedZ;
        const traveled = startZ - currentZ;

        if (traveled >= distanceZ) {
            // Transition complete - clear previous params
            this.biomeTransition.active = false;
            this.biomeTransition.progress = 1.0;
            this.biomeTransition.previous = null;
            console.log(`[BiomeRenderer] Biome transition complete (traveled ${traveled} units)`);
        } else {
            // Update progress based on Z distance
            this.biomeTransition.progress = traveled / distanceZ;
        }
    }

    /**
     * Update water level to ensure it's always below the lowest road point
     */
    updateWaterLevelFromRoad() {
        if (!this.pathPoints || this.pathPoints.length === 0) return;

        // Find the lowest Y value among all path points
        let lowestRoadY = Infinity;
        for (const point of this.pathPoints) {
            if (point.y < lowestRoadY) {
                lowestRoadY = point.y;
            }
        }

        // Water should be well below the lowest road point
        // Use configured waterLevel as an offset below the road minimum
        const waterOffset = Math.abs(this.waterLevel) || 30; // Default 30 units below
        const calculatedWaterLevel = lowestRoadY - waterOffset;

        // Store the effective water level
        this.effectiveWaterLevel = calculatedWaterLevel;
    }

    createWaterPlane() {
        // Use effective water level (calculated from road) or configured level
        const waterY = this.effectiveWaterLevel !== undefined ? this.effectiveWaterLevel : this.waterLevel;

        // Only create water if waterLevel is reasonable (not too far below terrain)
        if (waterY < -200) return;

        const waterGeometry = new THREE.PlaneGeometry(8000, 8000, 1, 1);
        const waterMaterial = new THREE.MeshStandardMaterial({
            color: 0x1e90ff,
            transparent: true,
            opacity: 0.6,
            metalness: 0.8,
            roughness: 0.2,
            side: THREE.DoubleSide
        });

        this.waterPlane = new THREE.Mesh(waterGeometry, waterMaterial);
        this.waterPlane.rotation.x = -Math.PI / 2;
        this.waterPlane.position.y = waterY;
        this.waterPlane.position.x = 0;
        this.waterPlane.position.z = 0;
        this.waterPlane.receiveShadow = true;
        // Add to scene directly (not worldTranslator) so it stays with camera
        this.scene.add(this.waterPlane);
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // EVENT-BASED ROAD GENERATION
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Seeded random number generator for deterministic road generation
     */
    _seededRandom() {
        this.eventSeed = (this.eventSeed * 1664525 + 1013904223) % 4294967296;
        return this.eventSeed / 4294967296;
    }

    /**
     * Seeded random that returns -1 to 1
     */
    _seededRandomSigned() {
        return this._seededRandom() * 2 - 1;
    }

    /**
     * Simple hash for position-based randomness
     */
    _posHash(n) {
        let x = ((n + this.eventSeed) * 374761393) | 0;
        x = ((x >> 17) ^ x) * 668265263 | 0;
        x = ((x >> 15) ^ x) * 2246822519 | 0;
        x = (x >> 13) ^ x;
        return ((x & 0x7fffffff) / 0x7fffffff) * 2 - 1;  // -1 to 1
    }

    /**
     * Get road position using direct noise - no interpolation, no spline artifacts
     * Uses multiple incommensurate frequencies to avoid patterns
     */
    _getRoadPositionAtZ(z) {
        const absZ = Math.abs(z);

        // Use multiple hash lookups at incommensurate intervals
        // The key is using prime/irrational-ish ratios that never sync up
        const lat1 = this._posHash(Math.floor(absZ / 3571)) * 0.5;  // Very long wave
        const lat2 = this._posHash(Math.floor(absZ / 1847) + 10000) * 0.3;  // Medium wave
        const lat3 = this._posHash(Math.floor(absZ / 743) + 20000) * 0.15;  // Shorter wave
        const lat4 = this._posHash(Math.floor(absZ / 293) + 30000) * 0.05;  // Detail

        const vert1 = this._posHash(Math.floor(absZ / 2903) + 50000) * 0.5;
        const vert2 = this._posHash(Math.floor(absZ / 1279) + 60000) * 0.35;
        const vert3 = this._posHash(Math.floor(absZ / 509) + 70000) * 0.15;

        // Get blended road offsets during biome transition
        // CRITICAL: Road position must be consistent across chunks during transitions
        const lateralOffset = this._getBlendedRoadParam('roadMaxLateralOffset', z);
        const verticalOffset = this._getBlendedRoadParam('roadMaxVerticalOffset', z);

        return {
            x: (lat1 + lat2 + lat3 + lat4) * lateralOffset,
            y: (vert1 + vert2 + vert3) * verticalOffset
        };
    }

    /**
     * Get blended road parameter during biome transition.
     * Simple lerp based on Z position - same as terrain params.
     */
    _getBlendedRoadParam(param, z) {
        const currentValue = this[param];

        // If no previous params stored, return current value
        if (!this.biomeTransition.previous) {
            return currentValue;
        }

        const previousValue = this.biomeTransition.previous[param];
        if (previousValue === undefined || previousValue === currentValue) {
            return currentValue;
        }

        // Calculate interpolation factor based on Z distance from transition start
        const startZ = this.biomeTransition.startZ;
        const distanceZ = this.biomeTransition.distanceZ;

        // Distance traveled since transition started (Z decreases as we go forward)
        const traveled = startZ - z;

        // Simple lerp: t=0 at start, t=1 at distanceZ
        let t = Math.max(0, Math.min(1, traveled / distanceZ));

        // Apply smooth easing
        const smoothT = t * t * (3 - 2 * t);

        return previousValue + (currentValue - previousValue) * smoothT;
    }

    generateRoadPath() {
        // Generate control points at regular intervals
        for (let i = 0; i < this.pathLookAhead; i++) {
            const z = -i * this.pathSegmentLength;
            const pos = this._getRoadPositionAtZ(z);
            this.pathPoints.push(new THREE.Vector3(pos.x, pos.y, z));
        }
        this.roadTotalZ = -this.pathLookAhead * this.pathSegmentLength;

        // Create a smooth curve through the points
        this.roadPath = new THREE.CatmullRomCurve3(this.pathPoints);
        this.roadPath.curveType = 'catmullrom';
        this.roadPath.tension = this.roadSplineTension;
    }

    extendRoadPath() {
        // Add a new point to the end of the path
        const lastPoint = this.pathPoints[this.pathPoints.length - 1];
        const z = lastPoint.z - this.pathSegmentLength;
        this.roadTotalZ = z;

        // Get position using macro path + micro noise
        const pos = this._getRoadPositionAtZ(z);
        this.pathPoints.push(new THREE.Vector3(pos.x, pos.y, z));

        // Remove old points that are far behind camera
        // Keep enough points to cover visible chunks plus buffer
        // IMPORTANT: Must keep enough points so we never need to extrapolate p0 for camera position
        // Camera is at worldZ = -groupOffset, need points well behind that for smooth interpolation
        const groupOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;
        while (this.pathPoints.length > 20 && this.pathPoints[0].z + groupOffset > 1500) {
            this.pathPoints.shift();
        }

        // Recreate the curve
        this.roadPath = new THREE.CatmullRomCurve3(this.pathPoints);
        this.roadPath.curveType = 'catmullrom';
        this.roadPath.tension = this.roadSplineTension;
    }

    getRoadPositionAtZ(z) {
        // First try to get position from centerline cache (exact positions used to render road)
        // This ensures camera tracks the actual rendered road, not re-computed spline positions
        const cachedResult = this._getFromCenterlineCache(z);
        if (cachedResult) {
            return cachedResult;
        }

        // Fallback to CatmullRom interpolation for positions not yet rendered
        // (i.e., ahead of the camera where road hasn't been created yet)
        return this._getRoadPositionFromSpline(z);
    }

    /**
     * Get road position from centerline cache with linear interpolation
     * Returns null if z is outside the cached range
     */
    _getFromCenterlineCache(z) {
        const cache = this.roadCenterlineCache;
        if (cache.length < 2) {
            return null;
        }

        // Cache is sorted from highest Z to lowest Z
        const highestZ = cache[0].z;
        const lowestZ = cache[cache.length - 1].z;

        // Z is ahead of cached data - fall back to spline
        if (z < lowestZ) {
            return null;
        }

        // Z is behind all cached data - use first cached entry (extrapolate backward)
        if (z > highestZ) {
            // Use the first cached entry's position but adjust Z
            const entry = cache[0];
            return {
                position: new THREE.Vector3(entry.position.x, entry.position.y, z),
                tangent: entry.tangent.clone()
            };
        }

        // Binary search to find bracketing entries
        let low = 0;
        let high = cache.length - 1;

        while (high - low > 1) {
            const mid = (low + high) >>> 1;
            if (cache[mid].z > z) {
                low = mid;
            } else {
                high = mid;
            }
        }

        // Entries: cache[low].z >= z >= cache[high].z
        const entryHigh = cache[low];   // Higher Z (behind)
        const entryLow = cache[high];   // Lower Z (ahead)

        // Calculate interpolation factor
        const dz = entryHigh.z - entryLow.z;
        if (Math.abs(dz) < 0.001) {
            // Entries are at same Z, return either one
            return {
                position: entryHigh.position.clone(),
                tangent: entryHigh.tangent.clone()
            };
        }

        const t = (z - entryLow.z) / dz;

        // Linear interpolation between cached positions
        const position = new THREE.Vector3(
            entryLow.position.x + (entryHigh.position.x - entryLow.position.x) * t,
            entryLow.position.y + (entryHigh.position.y - entryLow.position.y) * t,
            z
        );

        // Interpolate tangent and normalize
        const tangent = new THREE.Vector3(
            entryLow.tangent.x + (entryHigh.tangent.x - entryLow.tangent.x) * t,
            entryLow.tangent.y + (entryHigh.tangent.y - entryLow.tangent.y) * t,
            entryLow.tangent.z + (entryHigh.tangent.z - entryLow.tangent.z) * t
        ).normalize();

        if (tangent.lengthSq() < 0.0001) {
            tangent.set(0, 0, -1);
        }

        return { position, tangent };
    }

    /**
     * Get road position using CatmullRom spline interpolation
     * Used when generating new road chunks or querying positions ahead of cached data
     */
    _getRoadPositionFromSpline(z) {
        // Get road position and orientation at a given z coordinate
        // Uses LOCAL CatmullRom interpolation with only 4 nearby control points
        // This avoids spline instability when points are added/removed far away
        if (!this.pathPoints || this.pathPoints.length < 2) {
            return {
                position: new THREE.Vector3(0, 0, z),
                tangent: new THREE.Vector3(0, 0, -1)
            };
        }

        const numPoints = this.pathPoints.length;

        // Find the segment containing this Z value
        let segmentIdx = 0;
        for (let i = 0; i < numPoints - 1; i++) {
            if (z <= this.pathPoints[i].z && z >= this.pathPoints[i + 1].z) {
                segmentIdx = i;
                break;
            }
        }

        // Clamp to valid range
        if (z > this.pathPoints[0].z) {
            segmentIdx = 0;
        } else if (z < this.pathPoints[numPoints - 1].z) {
            segmentIdx = numPoints - 2;
        }

        // Get the 4 control points for local CatmullRom interpolation
        // p0 and p3 are the outer points, p1 and p2 are the segment endpoints
        const i1 = segmentIdx;
        const i2 = Math.min(numPoints - 1, segmentIdx + 1);

        const p1 = this.pathPoints[i1];
        const p2 = this.pathPoints[i2];

        // For edge cases, extrapolate p0 and p3 instead of clamping
        // This ensures smooth interpolation at segment boundaries
        let p0, p3;

        if (segmentIdx > 0) {
            p0 = this.pathPoints[segmentIdx - 1];
        } else {
            // Extrapolate p0 from p1 and p2
            p0 = new THREE.Vector3(
                p1.x * 2 - p2.x,
                p1.y * 2 - p2.y,
                p1.z * 2 - p2.z
            );
        }

        if (segmentIdx + 2 < numPoints) {
            p3 = this.pathPoints[segmentIdx + 2];
        } else {
            // Extrapolate p3 from p1 and p2
            p3 = new THREE.Vector3(
                p2.x * 2 - p1.x,
                p2.y * 2 - p1.y,
                p2.z * 2 - p1.z
            );
        }

        // Calculate local t within this segment (0-1)
        const segmentDz = p2.z - p1.z;
        let t = 0;
        if (Math.abs(segmentDz) > 0.001) {
            t = (z - p1.z) / segmentDz;
            t = Math.max(0, Math.min(1, t));
        }

        // CatmullRom interpolation formula with tension
        // Using the same tension as the global spline
        const tension = this.roadSplineTension;
        const t2 = t * t;
        const t3 = t2 * t;

        // CatmullRom basis functions with tension
        const s = (1 - tension) / 2;
        const b0 = -s * t3 + 2 * s * t2 - s * t;
        const b1 = (2 - s) * t3 + (s - 3) * t2 + 1;
        const b2 = (s - 2) * t3 + (3 - 2 * s) * t2 + s * t;
        const b3 = s * t3 - s * t2;

        // Interpolate position
        const position = new THREE.Vector3(
            b0 * p0.x + b1 * p1.x + b2 * p2.x + b3 * p3.x,
            b0 * p0.y + b1 * p1.y + b2 * p2.y + b3 * p3.y,
            z  // Use exact Z
        );

        // Calculate tangent (derivative of the basis functions)
        const db0 = -3 * s * t2 + 4 * s * t - s;
        const db1 = 3 * (2 - s) * t2 + 2 * (s - 3) * t;
        const db2 = 3 * (s - 2) * t2 + 2 * (3 - 2 * s) * t + s;
        const db3 = 3 * s * t2 - 2 * s * t;

        const tangent = new THREE.Vector3(
            db0 * p0.x + db1 * p1.x + db2 * p2.x + db3 * p3.x,
            db0 * p0.y + db1 * p1.y + db2 * p2.y + db3 * p3.y,
            db0 * p0.z + db1 * p1.z + db2 * p2.z + db3 * p3.z
        ).normalize();

        // Guard against zero-length tangent
        if (tangent.lengthSq() < 0.0001) {
            tangent.set(0, 0, -1);
        }

        return { position, tangent };
    }

    /**
     * Get road position data including position, tangent, and right vector.
     * WARNING: Returns pre-allocated vectors that will be overwritten on the next call.
     * Callers that need to keep the values should clone them.
     * @param {number} targetZ - Z position to query
     * @returns {Object|null} { position, tangent, right } or null if not found
     */
    getRoadDataAtZ(targetZ) {
        // Get road position data including position, tangent, and right vector
        const roadData = this.getRoadPositionAtZ(targetZ);

        if (!roadData) {
            return null;
        }

        // Calculate right vector using pre-allocated vectors (reduces GC pressure)
        this._roadDataRight.crossVectors(roadData.tangent, this._roadDataUp).normalize();

        // Reuse result object with updated references
        this._roadDataResult.position = roadData.position;
        this._roadDataResult.tangent = roadData.tangent;
        // right already points to _roadDataRight

        return this._roadDataResult;
    }

    sampleRoadSegment(zStart, zEnd, segments) {
        // Sample points along the road spline for this segment
        const roadData = [];

        // Round Z values to avoid floating-point precision issues with cache keys
        const roundZ = (z) => Math.round(z * 1000) / 1000;
        const zStartRounded = roundZ(zStart);

        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            // Calculate exact Z, but use zStart directly for first point to match cache
            const z = i === 0 ? zStart : zStart + (zEnd - zStart) * t;
            const zKey = i === 0 ? zStartRounded : roundZ(z);

            // Check if we have a cached boundary for this Z (for seamless chunk connections)
            // This ensures ALL samplings (road, high-res terrain, low-res terrain) use
            // the exact same boundary positions for perfect alignment
            const cachedData = this.chunkBoundaryCache.get(zKey);
            if (cachedData && i === 0) {
                // Use cached data for the first point to ensure perfect alignment with previous chunk
                roadData.push({
                    position: cachedData.position.clone(),
                    tangent: cachedData.tangent.clone(),
                    right: cachedData.right.clone(),
                    surfaceNormal: cachedData.surfaceNormal.clone(),
                    z: zStart // Use exact zStart, not cached z
                });
                continue;
            }

            // Always use spline for generating road geometry (not cache)
            // This ensures consistency - we cache these positions for later camera queries
            const roadInfo = this._getRoadPositionFromSpline(z);
            const position = roadInfo.position;
            const tangent = roadInfo.tangent.normalize();

            // Calculate right vector perpendicular to tangent
            // Project tangent onto XZ plane first to get horizontal direction
            const horizontalTangent = new THREE.Vector3(tangent.x, 0, tangent.z).normalize();

            // Right vector is perpendicular to horizontal tangent (in XZ plane)
            // This keeps road markings flat on the road surface regardless of slope
            let right = new THREE.Vector3(-horizontalTangent.z, 0, horizontalTangent.x);

            // Guard against degenerate case where tangent is purely vertical
            if (right.lengthSq() < 0.0001) {
                right.set(1, 0, 0);
            }

            // Calculate surface normal (cross product of right and tangent)
            // This gives the true "up" direction for the road surface at this point
            const surfaceNormal = new THREE.Vector3().crossVectors(right, tangent).normalize();

            const entry = { position, tangent, right, surfaceNormal, z };
            roadData.push(entry);

            // Cache centerline position for camera queries
            // Store position and tangent (cloned to avoid mutation)
            this._addToCenterlineCache(z, position.clone(), tangent.clone());

            // Cache the last point for the next chunk to use as its first point
            // Use rounded Z as key to avoid floating-point precision issues
            if (i === segments) {
                const zEndKey = roundZ(zEnd);
                // Clone all vectors to avoid mutation issues
                this.chunkBoundaryCache.set(zEndKey, {
                    position: position.clone(),
                    tangent: tangent.clone(),
                    right: right.clone(),
                    surfaceNormal: surfaceNormal.clone(),
                    z: zEnd
                });
                // Clean up old cache entries (keep only recent ones)
                if (this.chunkBoundaryCache.size > 200) {
                    const firstKey = this.chunkBoundaryCache.keys().next().value;
                    this.chunkBoundaryCache.delete(firstKey);
                }
            }
        }

        return roadData;
    }

    /**
     * Add a road centerline position to the cache
     * Maintains sorted order from highest Z to lowest Z
     *
     * Optimized to minimize array operations:
     * - New entries are typically at the end (lower Z values for ahead chunks)
     * - Uses push() for common case instead of splice()
     * - Batch-trims from front only when significantly oversized
     */
    _addToCenterlineCache(z, position, tangent) {
        const entry = { z, position, tangent };
        const cache = this.roadCenterlineCache;

        // Fast path: if cache is empty or new entry goes at end (most common case)
        // Since we generate chunks progressively forward, new entries typically have lower Z
        if (cache.length === 0 || z < cache[cache.length - 1].z - 0.001) {
            cache.push(entry);
            this._maybeTrimCenterlineCache();
            return;
        }

        // Check for duplicate at the end (common case when re-generating same chunk boundary)
        if (Math.abs(cache[cache.length - 1].z - z) < 0.001) {
            return; // Already cached at this Z
        }

        // Binary search to find insertion point (sorted descending by Z)
        let low = 0;
        let high = cache.length;

        while (low < high) {
            const mid = (low + high) >>> 1;
            if (cache[mid].z > z) {
                low = mid + 1;
            } else {
                high = mid;
            }
        }

        // Check for duplicate Z at insertion point
        if (low < cache.length && Math.abs(cache[low].z - z) < 0.001) {
            return; // Already cached at this Z
        }

        // Insert at correct position to maintain sorted order
        cache.splice(low, 0, entry);
        this._maybeTrimCenterlineCache();
    }

    /**
     * Trim centerline cache if it exceeds max size
     * Uses batch removal to minimize splice operations
     */
    _maybeTrimCenterlineCache() {
        const maxCacheSize = 5000;
        const trimThreshold = 5500; // Only trim when significantly over
        const cache = this.roadCenterlineCache;

        if (cache.length > trimThreshold) {
            // Remove oldest entries (highest Z values, at front of array)
            const removeCount = cache.length - maxCacheSize;
            cache.splice(0, removeCount);
        }
    }

    /**
     * Clean up old centerline cache entries behind the camera
     * Uses binary search + batch splice instead of repeated shift() for efficiency
     */
    cleanupCenterlineCache(behindZ) {
        const cache = this.roadCenterlineCache;
        if (cache.length === 0 || cache[0].z <= behindZ) {
            return; // Nothing to remove
        }

        // Binary search to find first entry with Z <= behindZ
        let low = 0;
        let high = cache.length;

        while (low < high) {
            const mid = (low + high) >>> 1;
            if (cache[mid].z > behindZ) {
                low = mid + 1;
            } else {
                high = mid;
            }
        }

        // Remove all entries before 'low' (which all have Z > behindZ)
        if (low > 0) {
            cache.splice(0, low);
        }
    }

    /**
     * Create the road shader material with procedural noise texture
     * Uses world-space coordinates for seamless chunk transitions
     */
    createRoadMaterial() {
        // Create base Lambert material with vertex colors
        const material = new THREE.MeshLambertMaterial({
            vertexColors: true
        });

        if (!this.roadTextureEnabled) {
            return material;
        }

        // Store texture parameters for the shader
        const textureStrength = this.roadTextureStrength;
        const scaleCoarse = this.roadTextureScaleCoarse;
        const coarseStrength = this.roadTextureCoarseStrength;
        const scaleFine = this.roadTextureScaleFine;
        const fineStrength = this.roadTextureFineStrength;

        // Simplex noise GLSL implementation
        const simplexNoiseGLSL = `
            vec4 permute(vec4 x) { return mod(((x*34.0)+1.0)*x, 289.0); }
            vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

            float snoise(vec3 v) {
                const vec2 C = vec2(1.0/6.0, 1.0/3.0);
                const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

                vec3 i  = floor(v + dot(v, C.yyy));
                vec3 x0 = v - i + dot(i, C.xxx);

                vec3 g = step(x0.yzx, x0.xyz);
                vec3 l = 1.0 - g;
                vec3 i1 = min(g.xyz, l.zxy);
                vec3 i2 = max(g.xyz, l.zxy);

                vec3 x1 = x0 - i1 + C.xxx;
                vec3 x2 = x0 - i2 + C.yyy;
                vec3 x3 = x0 - D.yyy;

                i = mod(i, 289.0);
                vec4 p = permute(permute(permute(
                    i.z + vec4(0.0, i1.z, i2.z, 1.0))
                    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
                    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

                float n_ = 1.0/7.0;
                vec3 ns = n_ * D.wyz - D.xzx;

                vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

                vec4 x_ = floor(j * ns.z);
                vec4 y_ = floor(j - 7.0 * x_);

                vec4 x = x_ *ns.x + ns.yyyy;
                vec4 y = y_ *ns.x + ns.yyyy;
                vec4 h = 1.0 - abs(x) - abs(y);

                vec4 b0 = vec4(x.xy, y.xy);
                vec4 b1 = vec4(x.zw, y.zw);

                vec4 s0 = floor(b0)*2.0 + 1.0;
                vec4 s1 = floor(b1)*2.0 + 1.0;
                vec4 sh = -step(h, vec4(0.0));

                vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
                vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;

                vec3 p0 = vec3(a0.xy, h.x);
                vec3 p1 = vec3(a0.zw, h.y);
                vec3 p2 = vec3(a1.xy, h.z);
                vec3 p3 = vec3(a1.zw, h.w);

                vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
                p0 *= norm.x;
                p1 *= norm.y;
                p2 *= norm.z;
                p3 *= norm.w;

                vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
                m = m * m;
                return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
            }
        `;

        // Modify the material's shaders using onBeforeCompile
        material.onBeforeCompile = (shader) => {
            // Add uniforms
            shader.uniforms.textureStrength = { value: textureStrength };
            shader.uniforms.scaleCoarse = { value: scaleCoarse };
            shader.uniforms.coarseStrength = { value: coarseStrength };
            shader.uniforms.scaleFine = { value: scaleFine };
            shader.uniforms.fineStrength = { value: fineStrength };

            // Store reference for dynamic updates
            material.userData.shader = shader;

            // Add varyings for local position and custom road UV
            shader.vertexShader = shader.vertexShader.replace(
                '#include <common>',
                `#include <common>
                attribute vec2 roadUv;
                varying vec3 vLocalPosition;
                varying vec2 vRoadUv;`
            );

            shader.vertexShader = shader.vertexShader.replace(
                '#include <worldpos_vertex>',
                `#include <worldpos_vertex>
                vLocalPosition = position;
                vRoadUv = roadUv;`
            );

            // Add noise function and modify fragment shader
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <common>',
                `#include <common>

                uniform float textureStrength;
                uniform float scaleCoarse;
                uniform float coarseStrength;
                uniform float scaleFine;
                uniform float fineStrength;

                varying vec3 vLocalPosition;
                varying vec2 vRoadUv;

                ${simplexNoiseGLSL}`
            );

            // Apply noise and grooves to the final color
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <dithering_fragment>',
                `// Apply road texture noise using local position (fixed to road geometry)
                vec3 noiseCoord = vec3(vLocalPosition.x * 2.0, vLocalPosition.z, 0.0);
                float noiseCoarse = snoise(noiseCoord * scaleCoarse) * coarseStrength;
                float noiseFine = snoise(noiseCoord * scaleFine) * fineStrength;
                float combinedNoise = (noiseCoarse + noiseFine) * textureStrength;

                // Longitudinal grooves using road-relative UV coordinates
                // vRoadUv.x = 0-1 across road width, vRoadUv.y = distance along road
                float grooveFrequency = 300.0;
                float grooveDepth = 0.015;

                // Subtle wobble to break up perfect parallel lines
                float xWobble = snoise(vec3(vRoadUv.y * 0.15, vRoadUv.x * 1.0, 0.0)) * 0.003;
                float wobbledX = vRoadUv.x + xWobble;

                // Very slight frequency variation along the road
                float freqVariation = 1.0 + snoise(vec3(vRoadUv.y * 0.05, 0.0, 10.0)) * 0.05;
                float grooveBase = sin(wobbledX * grooveFrequency * freqVariation * 3.14159);

                // Mild intensity wobble
                float grooveWobble = snoise(vec3(vRoadUv.x * 2.0, vRoadUv.y * 0.04, 0.0)) * 0.15;
                float groove = smoothstep(-0.35, 0.35, grooveBase + grooveWobble);
                float grooveEffect = (1.0 - groove) * grooveDepth;

                gl_FragColor.rgb += combinedNoise;
                gl_FragColor.rgb -= grooveEffect;
                gl_FragColor.rgb = clamp(gl_FragColor.rgb, 0.0, 1.0);

                #include <dithering_fragment>`
            );
        };

        return material;
    }

    /**
     * Update the road material's fog uniforms to match scene fog
     * With onBeforeCompile, fog is handled automatically by MeshLambertMaterial
     */
    updateRoadFog(fogColor, fogNear, fogFar) {
        // Fog is handled automatically by MeshLambertMaterial
        // No manual update needed
    }

    /**
     * Update road texture parameters dynamically
     * Useful for tweaking visuals at runtime
     */
    updateRoadTextureParams(params) {
        if (!this.roadMaterial || !this.roadMaterial.userData.shader) return;

        const uniforms = this.roadMaterial.userData.shader.uniforms;
        if (params.strength !== undefined) {
            uniforms.textureStrength.value = params.strength;
        }
        if (params.scaleCoarse !== undefined) {
            uniforms.scaleCoarse.value = params.scaleCoarse;
        }
        if (params.coarseStrength !== undefined) {
            uniforms.coarseStrength.value = params.coarseStrength;
        }
        if (params.scaleFine !== undefined) {
            uniforms.scaleFine.value = params.scaleFine;
        }
        if (params.fineStrength !== undefined) {
            uniforms.fineStrength.value = params.fineStrength;
        }
    }

    createRoadChunk(index, zStartOverride = null) {
        // Calculate chunk Z position
        // If zStartOverride is provided, use it (for chunks created after wrapping)
        // Otherwise calculate from index (for initial chunk creation)
        const zStart = zStartOverride !== null ? zStartOverride : -index * this.chunkLength;
        const zEnd = zStart - this.chunkLength;

        // Sample points along the road path for this chunk
        const segments = this.roadSegmentsPerChunk;
        const roadData = this.sampleRoadSegment(zStart, zEnd, segments);

        // Build road geometry from sampled points
        const roadGeometry = new THREE.BufferGeometry();
        const vertices = [];
        const indices = [];
        const normals = [];
        const colors = [];
        const uvs = []; // UV coordinates: U = across road, V = along road

        // Pre-compute base and variant colors for gradient
        const baseColor = new THREE.Color(this.roadColorBase);
        const variantColor = new THREE.Color(this.roadColorVariant);

        // Track cumulative distance along the road for V coordinate
        let cumulativeDistance = 0;

        for (let i = 0; i <= segments; i++) {
            const data = roadData[i];
            const { position, right, z } = data;

            // Calculate distance traveled along road for UV V coordinate
            if (i > 0) {
                const prevPos = roadData[i - 1].position;
                cumulativeDistance += position.distanceTo(prevPos);
            }

            // Create two vertices (left and right edge of road) - reuse temp vectors
            const leftPoint = this._tempVec1.copy(position).addScaledVector(right, -this.roadWidth / 2);
            const rightPoint = this._tempVec2.copy(position).addScaledVector(right, this.roadWidth / 2);

            vertices.push(leftPoint.x, leftPoint.y, leftPoint.z);
            vertices.push(rightPoint.x, rightPoint.y, rightPoint.z);

            normals.push(0, 1, 0);
            normals.push(0, 1, 0);

            // UV coordinates: U goes 0-1 across road width, V is distance along road
            // Left edge U=0, Right edge U=1
            uvs.push(0, cumulativeDistance); // Left vertex
            uvs.push(1, cumulativeDistance); // Right vertex

            // Calculate road color gradient based on noise at this Z position
            // Use local Z directly for consistent noise sampling
            const colorNoise = this.noise.noise(z * this.roadColorGradientScale, 300);
            const t = ((colorNoise + 1) / 2) * this.roadColorGradientStrength; // Normalize to 0-strength
            const vertexColor = baseColor.clone().lerp(variantColor, t);

            // Both left and right vertices get the same color
            colors.push(vertexColor.r, vertexColor.g, vertexColor.b);
            colors.push(vertexColor.r, vertexColor.g, vertexColor.b);

            // Create triangles (except for last segment)
            if (i < segments) {
                const baseIndex = i * 2;
                indices.push(baseIndex, baseIndex + 1, baseIndex + 2);
                indices.push(baseIndex + 1, baseIndex + 3, baseIndex + 2);
            }
        }

        roadGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        roadGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        roadGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        roadGeometry.setAttribute('roadUv', new THREE.Float32BufferAttribute(uvs, 2));
        roadGeometry.setIndex(indices);
        // Note: Skipping computeVertexNormals() - flat road normals already set to (0,1,0) above

        // Use shared road material (with procedural texture shader)
        const road = new THREE.Mesh(roadGeometry, this.roadMaterial);
        road.receiveShadow = true;
        road.castShadow = false;

        this.worldTranslator.add(road);

        // Create markings using the same road data as the road surface
        // This ensures markings exactly follow the road geometry
        const markings = this.createRoadMarkings(roadData, zStart, zEnd);

        this.roadChunks.push({
            mesh: road,
            markings: markings,
            index: index,
            roadData: roadData,
            baseZ: zStart  // Store the chunk's starting Z for recycling calculations
        });
    }

    /**
     * Create road markings for a chunk using MUTCD-compliant system
     * - Both can pass: SINGLE centered dashed line
     * - Asymmetric: Two parallel lines (solid + dashed)
     * - Neither can pass: Double parallel SOLID lines
     */
    createRoadMarkings(roadData, zStart, zEnd) {
        const markings = [];

        if (!roadData || roadData.length < 2) return markings;

        // Calculate cumulative distances along the road
        const distances = this.calculateChunkDistances(roadData);
        const chunkLength = distances[distances.length - 1];

        // Analyze each segment for per-lane passing conditions
        const analysis = this.analyzePerLaneConditions(roadData);

        // Classify segments into zone types
        // Type: 'both_pass' | 'left_solid' | 'right_solid' | 'both_solid'
        const zoneTypes = analysis.map(a => {
            if (!a.leftCanPass && !a.rightCanPass) return 'both_solid';
            if (!a.leftCanPass) return 'left_solid';
            if (!a.rightCanPass) return 'right_solid';
            return 'both_pass';
        });

        // Find contiguous zones with hysteresis and minimum length enforcement
        const zones = this.findMarkingZones(zoneTypes, distances);

        // Offsets for double lines
        const leftOffset = -this.centerLineSpacing / 2;
        const rightOffset = this.centerLineSpacing / 2;

        for (const zone of zones) {
            const { startIdx, endIdx, type } = zone;

            if (type === 'both_pass') {
                // Single centered dashed line
                const startDist = this.dashPatternState.cumulativeDistance + distances[startIdx];
                const dashes = this.createDashedSegment(roadData, distances, startIdx, endIdx, 0, startDist);
                dashes.forEach(d => {
                    this.worldTranslator.add(d);
                    markings.push(d);
                });
            } else if (type === 'both_solid') {
                // Double parallel solid lines
                const leftLine = this.createSingleDashSegment(roadData, startIdx, endIdx, leftOffset);
                const rightLine = this.createSingleDashSegment(roadData, startIdx, endIdx, rightOffset);
                if (leftLine) {
                    this.worldTranslator.add(leftLine);
                    markings.push(leftLine);
                }
                if (rightLine) {
                    this.worldTranslator.add(rightLine);
                    markings.push(rightLine);
                }
            } else if (type === 'left_solid') {
                // Left solid, right dashed
                const leftLine = this.createSingleDashSegment(roadData, startIdx, endIdx, leftOffset);
                if (leftLine) {
                    this.worldTranslator.add(leftLine);
                    markings.push(leftLine);
                }
                const startDist = this.dashPatternState.cumulativeDistance + distances[startIdx];
                const dashes = this.createDashedSegment(roadData, distances, startIdx, endIdx, rightOffset, startDist);
                dashes.forEach(d => {
                    this.worldTranslator.add(d);
                    markings.push(d);
                });
            } else if (type === 'right_solid') {
                // Right solid, left dashed
                const rightLine = this.createSingleDashSegment(roadData, startIdx, endIdx, rightOffset);
                if (rightLine) {
                    this.worldTranslator.add(rightLine);
                    markings.push(rightLine);
                }
                const startDist = this.dashPatternState.cumulativeDistance + distances[startIdx];
                const dashes = this.createDashedSegment(roadData, distances, startIdx, endIdx, leftOffset, startDist);
                dashes.forEach(d => {
                    this.worldTranslator.add(d);
                    markings.push(d);
                });
            }
        }

        // Update cumulative distance for next chunk's dash continuity
        this.dashPatternState.cumulativeDistance += chunkLength;

        // Edge lines (continuous white)
        const leftEdge = this.createEdgeLine(roadData, -this.roadWidth / 2);
        const rightEdge = this.createEdgeLine(roadData, this.roadWidth / 2);

        if (leftEdge) {
            this.worldTranslator.add(leftEdge);
            markings.push(leftEdge);
        }
        if (rightEdge) {
            this.worldTranslator.add(rightEdge);
            markings.push(rightEdge);
        }

        return markings;
    }

    /**
     * Find contiguous marking zones with hysteresis to prevent rapid switching
     * Also enforces minimum zone length and carries state across chunks
     */
    findMarkingZones(zoneTypes, distances) {
        const zones = [];
        if (zoneTypes.length === 0) return zones;

        // Start with the last zone type from previous chunk for continuity
        let currentType = this.markingZoneState.lastZoneType;
        let currentLength = this.markingZoneState.lastZoneLength;
        let zoneStart = 0;
        let pendingType = null;
        let pendingCount = 0;

        // Check if first segment matches carried-over type
        // If not, we need to decide whether to continue the previous type or switch
        if (zoneTypes[0] !== currentType) {
            // Count how many segments at the start match a different type
            let newTypeCount = 0;
            for (let i = 0; i < zoneTypes.length && zoneTypes[i] !== currentType; i++) {
                newTypeCount++;
            }

            // Only switch if the new type has enough segments AND previous zone was long enough
            if (newTypeCount >= this.minZoneSegments && currentLength >= this.minZoneLength) {
                currentType = zoneTypes[0];
                currentLength = 0;
            }
            // Otherwise, keep the previous type (short anomalies get absorbed)
        }

        for (let i = 1; i < zoneTypes.length; i++) {
            const segmentType = zoneTypes[i];

            if (segmentType !== currentType) {
                if (pendingType === segmentType) {
                    pendingCount++;
                    if (pendingCount >= this.minZoneSegments) {
                        // Check if current zone meets minimum length before committing change
                        const zoneEndIdx = i - pendingCount;
                        const zoneLength = currentLength + (distances[zoneEndIdx] - distances[zoneStart]);

                        if (zoneLength >= this.minZoneLength) {
                            // Commit zone change
                            zones.push({
                                startIdx: zoneStart,
                                endIdx: zoneEndIdx,
                                type: currentType
                            });
                            zoneStart = zoneEndIdx + 1;
                            currentType = segmentType;
                            currentLength = 0;
                            pendingType = null;
                            pendingCount = 0;
                        } else {
                            // Zone too short - keep absorbing into current type
                            pendingType = null;
                            pendingCount = 0;
                        }
                    }
                } else {
                    pendingType = segmentType;
                    pendingCount = 1;
                }
            } else {
                pendingType = null;
                pendingCount = 0;
            }
        }

        // Close final zone
        zones.push({
            startIdx: zoneStart,
            endIdx: zoneTypes.length - 1,
            type: currentType
        });

        // Update state for next chunk
        const finalZoneLength = distances[zoneTypes.length - 1] - distances[zoneStart];
        this.markingZoneState.lastZoneType = currentType;
        this.markingZoneState.lastZoneLength = currentLength + finalZoneLength;

        return zones;
    }

    /**
     * Calculate cumulative distances along road segments
     */
    calculateChunkDistances(roadData) {
        const distances = [0];
        for (let i = 1; i < roadData.length; i++) {
            const segLen = roadData[i - 1].position.distanceTo(roadData[i].position);
            distances.push(distances[i - 1] + segLen);
        }
        return distances;
    }

    /**
     * Analyze road segments for per-lane passing conditions
     * Returns array with {leftCanPass, rightCanPass} for each segment
     */
    analyzePerLaneConditions(roadData) {
        const analysis = [];
        let maxCurvature = 0;

        // Use a wider window for curvature detection (look 8 points ahead/behind)
        const curvatureWindow = 8;

        // First pass: calculate local curvature and slope for each segment
        for (let i = 0; i < roadData.length; i++) {
            // For curvature, look at direction change over a wider window
            const prevIdx = Math.max(0, i - curvatureWindow);
            const nextIdx = Math.min(roadData.length - 1, i + curvatureWindow);

            const prev = roadData[prevIdx];
            const curr = roadData[i];
            const next = roadData[nextIdx];

            // Calculate curvature using direction change over wider window
            let curvature = 0;
            if (i >= curvatureWindow && i < roadData.length - curvatureWindow) {
                const tangent1 = new THREE.Vector3()
                    .subVectors(curr.position, prev.position);
                const tangent2 = new THREE.Vector3()
                    .subVectors(next.position, curr.position);

                const len1 = tangent1.length();
                const len2 = tangent2.length();

                if (len1 > 0.01 && len2 > 0.01) {
                    tangent1.divideScalar(len1);
                    tangent2.divideScalar(len2);
                    curvature = 1 - tangent1.dot(tangent2);
                }
            }
            maxCurvature = Math.max(maxCurvature, curvature);

            // Calculate slope using immediate neighbors for more local detection
            const slopePrev = roadData[Math.max(0, i - 1)];
            const slopeNext = roadData[Math.min(roadData.length - 1, i + 1)];
            const dist = slopePrev.position.distanceTo(slopeNext.position);
            const slope = dist > 0 ? (slopeNext.position.y - slopePrev.position.y) / dist : 0;

            // Determine passing ability for each lane
            let leftCanPass = true;   // Oncoming lane
            let rightCanPass = true;  // Player lane

            // Sharp curve - neither can pass (double solid)
            if (curvature > this.noPassingCurvatureThreshold) {
                leftCanPass = false;
                rightCanPass = false;
            }
            // Very steep slope - neither can pass (double solid)
            // Use 3x the threshold for double-solid (only extreme grades like 18%+)
            // Most hills should get asymmetric markings instead
            else if (Math.abs(slope) > this.noPassingSlopeThreshold * 3.0) {
                leftCanPass = false;
                rightCanPass = false;
            }
            // Moderate uphill (for player) - player can't see over crest
            else if (slope > this.noPassingSlopeThreshold) {
                rightCanPass = false;  // Player can't pass (going uphill)
                leftCanPass = true;    // Oncoming can pass (they're going downhill)
            }
            // Moderate downhill (for player) - oncoming (going uphill) can't see
            else if (slope < -this.noPassingSlopeThreshold) {
                rightCanPass = true;   // Player can pass (going downhill)
                leftCanPass = false;   // Oncoming can't pass (they're going uphill)
            }

            analysis.push({ leftCanPass, rightCanPass, curvature, slope });
        }

        // Second pass: look-ahead scan - mark segments as no-passing if there's
        // a dangerous curve/hill coming up that you need to see to pass safely
        const lookAhead = this.noPassingLookAhead;
        for (let i = 0; i < analysis.length; i++) {
            // Scan ahead for upcoming hazards
            for (let j = 1; j <= lookAhead && i + j < analysis.length; j++) {
                const ahead = analysis[i + j];
                // If upcoming segment has no-passing for a lane, propagate backwards
                // (you can't start a pass if there's a curve/hill coming up)
                if (!ahead.rightCanPass && analysis[i].rightCanPass) {
                    // Check if the hazard ahead is significant (not just single-lane restriction)
                    if (!ahead.leftCanPass || ahead.curvature > this.noPassingCurvatureThreshold * 0.5) {
                        analysis[i].rightCanPass = false;
                    }
                }
                if (!ahead.leftCanPass && analysis[i].leftCanPass) {
                    if (!ahead.rightCanPass || ahead.curvature > this.noPassingCurvatureThreshold * 0.5) {
                        analysis[i].leftCanPass = false;
                    }
                }
            }
        }

        return analysis;
    }

    /**
     * Create dashed line segment with proper phase continuity across chunks
     */
    createDashedSegment(roadData, distances, startIdx, endIdx, lateralOffset, globalStartDistance) {
        const dashes = [];
        const patternLength = this.dashLength + this.dashGapLength;

        // Calculate local distances within this segment
        const segmentStartDist = distances[startIdx];
        const segmentEndDist = distances[endIdx];
        const segmentLength = segmentEndDist - segmentStartDist;

        if (segmentLength <= 0) return dashes;

        // Calculate phase based on global distance for continuity
        const phase = globalStartDistance % patternLength;

        // Determine where first dash starts within this segment
        let localPos;
        if (phase < this.dashLength) {
            // We're inside a dash from previous chunk - continue it
            localPos = 0;
        } else {
            // We're in a gap - calculate when next dash starts
            localPos = patternLength - phase;
        }

        // Helper to find road index at a given distance
        const findIndexAtDist = (targetDist) => {
            const absoluteDist = segmentStartDist + targetDist;
            for (let i = startIdx; i < endIdx; i++) {
                if (distances[i + 1] >= absoluteDist) {
                    return i;
                }
            }
            return endIdx - 1;
        };

        while (localPos < segmentLength) {
            const dashStart = localPos;
            const dashEnd = Math.min(localPos + this.dashLength, segmentLength);

            const dashStartIdx = findIndexAtDist(dashStart);
            const dashEndIdx = findIndexAtDist(dashEnd);

            // Only create dash if we have valid indices
            if (dashEndIdx > dashStartIdx) {
                const dash = this.createSingleDashSegment(roadData, dashStartIdx, dashEndIdx, lateralOffset);
                if (dash) dashes.push(dash);
            }

            localPos += patternLength;
        }

        return dashes;
    }

    /**
     * Create a single dash at specified lateral offset
     */
    createSingleDashSegment(roadData, startIdx, endIdx, lateralOffset) {
        if (endIdx - startIdx < 1) return null;

        const vertices = [];
        const normals = [];
        const indices = [];
        const markingOffset = 0.02;
        const lineWidth = this.lineWidth;

        for (let i = startIdx; i <= endIdx; i++) {
            if (i >= roadData.length) break;

            const { position, right, surfaceNormal } = roadData[i];

            // Use fresh vectors to avoid any shared state issues
            const leftPt = new THREE.Vector3()
                .copy(position)
                .addScaledVector(right, lateralOffset - lineWidth / 2);
            const rightPt = new THREE.Vector3()
                .copy(position)
                .addScaledVector(right, lateralOffset + lineWidth / 2);

            vertices.push(
                leftPt.x + surfaceNormal.x * markingOffset,
                leftPt.y + surfaceNormal.y * markingOffset,
                leftPt.z + surfaceNormal.z * markingOffset
            );
            vertices.push(
                rightPt.x + surfaceNormal.x * markingOffset,
                rightPt.y + surfaceNormal.y * markingOffset,
                rightPt.z + surfaceNormal.z * markingOffset
            );

            normals.push(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);
            normals.push(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);

            if (i > startIdx) {
                const baseIdx = (i - startIdx) * 2;
                indices.push(baseIdx - 2, baseIdx - 1, baseIdx);
                indices.push(baseIdx - 1, baseIdx + 1, baseIdx);
            }
        }

        if (vertices.length < 4) return null;

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        geometry.setIndex(indices);

        const material = new THREE.MeshLambertMaterial({
            color: 0xffff00,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1
        });

        return new THREE.Mesh(geometry, material);
    }

    createEdgeLine(roadData, offset) {
        // Guard against empty road data
        if (!roadData || roadData.length < 2) {
            return null;
        }

        const vertices = [];
        const normals = [];
        const indices = [];
        const markingOffset = 0.02;
        const lineWidth = 0.15;

        for (let i = 0; i < roadData.length; i++) {
            const { position, right, surfaceNormal } = roadData[i];

            const innerPt = this._tempVec1.copy(position).addScaledVector(right, offset - lineWidth / 2);
            const outerPt = this._tempVec2.copy(position).addScaledVector(right, offset + lineWidth / 2);

            vertices.push(
                innerPt.x + surfaceNormal.x * markingOffset,
                innerPt.y + surfaceNormal.y * markingOffset,
                innerPt.z + surfaceNormal.z * markingOffset
            );
            vertices.push(
                outerPt.x + surfaceNormal.x * markingOffset,
                outerPt.y + surfaceNormal.y * markingOffset,
                outerPt.z + surfaceNormal.z * markingOffset
            );

            normals.push(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);
            normals.push(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);

            if (i > 0) {
                const baseIdx = i * 2;
                indices.push(baseIdx - 2, baseIdx - 1, baseIdx);
                indices.push(baseIdx - 1, baseIdx + 1, baseIdx);
            }
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        geometry.setIndex(indices);

        const material = new THREE.MeshLambertMaterial({
            color: 0xffffff,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1
        });
        return new THREE.Mesh(geometry, material);
    }

    createTerrainChunk(index, zStartOverride = null) {
        // Get the road data from the already-created road chunk
        const roadChunk = this.roadChunks.find(chunk => chunk.index === index);
        if (!roadChunk || !roadChunk.roadData) {
            console.error('Road chunk not found for terrain generation:', index);
            return;
        }

        // Calculate chunk Z position
        // If zStartOverride is provided, use it (for chunks created after wrapping)
        // Otherwise calculate from index (for initial chunk creation)
        const zStart = zStartOverride !== null ? zStartOverride : -index * this.chunkLength;
        const zEnd = zStart - this.chunkLength;

        const chunkNoiseOffset = 0;

        // HIGH-RES road sampling for roadside strip (follows road curves precisely)
        const highResSegments = Math.max(15, Math.floor(this.chunkLength / 5)); // ~1 sample per 5 units
        const highResRoadData = this.sampleRoadSegment(zStart, zEnd, highResSegments);

        // Create high-res roadside strips that slope down to meet grid terrain
        const leftStrip = this.createRoadsideStrip(highResRoadData, 'left', chunkNoiseOffset);
        const rightStrip = this.createRoadsideStrip(highResRoadData, 'right', chunkNoiseOffset);
        this.worldTranslator.add(leftStrip);
        this.worldTranslator.add(rightStrip);

        // Create grid-based terrain (uniform world-space grid, no road-following)
        const leftMountain = this.createGridTerrain(zStart, zEnd, 'left', chunkNoiseOffset);
        const rightMountain = this.createGridTerrain(zStart, zEnd, 'right', chunkNoiseOffset);
        this.worldTranslator.add(leftMountain);
        this.worldTranslator.add(rightMountain);

        this.terrainChunks.push({
            leftStrip: leftStrip,
            rightStrip: rightStrip,
            leftBridge: null, // No bridge needed with grid terrain
            rightBridge: null,
            left: leftMountain,
            right: rightMountain,
            index: index,
            roadData: highResRoadData
        });

        // Advance biome transition after creating terrain chunk
        this._advanceBiomeTransition();
    }

    createTerrainFromRoadEdge(roadData, width, segmentsOut, side, noiseOffset = 0) {
        // Build terrain geometry starting from the road edge and extending outward
        const geometry = new THREE.BufferGeometry();
        const vertices = [];
        const indices = [];

        const edgeDirection = side === 'left' ? -1 : 1;

        // Overlap to prevent gaps on curves (terrain extends under road edge)
        const terrainOverlap = 0.1;

        // For each point along the road
        for (let i = 0; i < roadData.length; i++) {
            const { position, right, z } = roadData[i];

            // Road edge position with overlap inward
            const roadEdge = this._tempVec1.copy(position).addScaledVector(right, edgeDirection * (this.roadWidth / 2 - terrainOverlap));

            const noiseZ = noiseOffset + z;
            const distanceNoise = this.noise.noise(noiseZ * this.terrainDistanceNoiseScale, 500 + (side === 'left' ? 0 : 100));
            const normalizedNoise = (distanceNoise + 1) / 2;

            const flatZone = this.terrainFlatZoneMin + normalizedNoise * (this.terrainFlatZoneMax - this.terrainFlatZoneMin);
            const blendRange = this.terrainBlendRange;

            // Create vertices extending outward from road edge
            for (let j = 0; j <= segmentsOut; j++) {
                const t = j / segmentsOut;
                const distance = t * width;

                const vertexPos = this._tempVec2.copy(roadEdge).addScaledVector(right, edgeDirection * distance);

                let height;
                if (t < flatZone) {
                    height = roadEdge.y;
                } else {
                    let terrainHeight = this.generateTerrainHeightWithOffset(vertexPos.x, vertexPos.z, noiseOffset, distance, side);

                    if (t < flatZone + blendRange) {
                        const blendFactor = (t - flatZone) / blendRange;
                        const smoothBlend = blendFactor * blendFactor * (3 - 2 * blendFactor);
                        height = roadEdge.y * (1 - smoothBlend) + terrainHeight * smoothBlend;
                    } else {
                        height = terrainHeight;
                    }
                }

                vertices.push(vertexPos.x, height, vertexPos.z);

                // Create triangles (except for last row and column)
                if (i < roadData.length - 1 && j < segmentsOut) {
                    const current = i * (segmentsOut + 1) + j;
                    const next = (i + 1) * (segmentsOut + 1) + j;

                    if (side === 'left') {
                        indices.push(current, next, current + 1);
                        indices.push(next, next + 1, current + 1);
                    } else {
                        indices.push(current, current + 1, next);
                        indices.push(next, current + 1, next + 1);
                    }
                }
            }
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();

        const material = new THREE.MeshLambertMaterial({
            color: this._getBlendedTerrainColor(),
            flatShading: true
        });

        this.terrainMaterials.push(material);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.receiveShadow = true;
        mesh.castShadow = false;

        return mesh;
    }

    /**
     * Create high-resolution roadside strip that follows road curves precisely
     * This bridges the gap between road edge and low-poly mountain terrain
     */
    /**
     * Calculate curve clearance at a Z position - used by both strip and grid terrain
     * to ensure they meet exactly at the same edge
     */
    calculateCurveClearance(z, cellSizeZ = 100) {
        const roadAhead = this.getRoadDataAtZ(z - cellSizeZ);
        const roadBehind = this.getRoadDataAtZ(z + cellSizeZ);

        if (roadAhead && roadBehind) {
            // How much road moves laterally over 2 cells
            const curveDelta = Math.abs(roadAhead.position.x - roadBehind.position.x);
            // Add clearance proportional to curve severity (increased from 0.6 to 0.8)
            return curveDelta * 0.8;
        }
        return 0;
    }

    createRoadsideStrip(roadData, side, noiseOffset = 0) {
        const geometry = new THREE.BufferGeometry();
        const vertices = [];
        const indices = [];

        const edgeDirection = side === 'left' ? -1 : 1;
        const baseStripWidth = this._getRoadsideStripWidth(side);
        const stripSegments = this.roadsideStripSegments;

        // Small overlap to prevent gaps, but not enough to cause z-fighting
        const terrainOverlap = this.roadsideStripOverlap;

        // Grid terrain configuration - must match createGridTerrain
        const cellSizeZ = 50;  // Must match createGridTerrain for curve clearance alignment
        const stripOverlapIntoGrid = 15; // How much grid extends under strip

        for (let i = 0; i < roadData.length; i++) {
            const { position, right, z } = roadData[i];
            const roadEdge = this._tempVec1.copy(position).addScaledVector(right, edgeDirection * (this.roadWidth / 2 - terrainOverlap));

            // Calculate curve clearance at this Z to match grid terrain
            const curveExtraClearance = this.calculateCurveClearance(z, cellSizeZ);

            // Grid terrain starts at: roadWidth/2 + baseStripWidth - stripOverlapIntoGrid + curveExtraClearance
            // Strip must extend to meet grid, with overlap
            const gridInnerEdge = baseStripWidth - stripOverlapIntoGrid + curveExtraClearance + stripOverlapIntoGrid;
            const dynamicStripWidth = gridInnerEdge;

            // Create vertices from road edge out to dynamic strip width
            for (let j = 0; j <= stripSegments; j++) {
                const t = j / stripSegments;
                const distance = t * dynamicStripWidth;
                const vertexPos = this._tempVec2.copy(roadEdge).addScaledVector(right, edgeDirection * distance);

                // Strip is flat at road height - grid terrain handles the blending to terrain
                const height = position.y;
                vertices.push(vertexPos.x, height, vertexPos.z);

                if (i < roadData.length - 1 && j < stripSegments) {
                    const current = i * (stripSegments + 1) + j;
                    const next = (i + 1) * (stripSegments + 1) + j;

                    if (side === 'left') {
                        indices.push(current, next, current + 1);
                        indices.push(next, next + 1, current + 1);
                    } else {
                        indices.push(current, current + 1, next);
                        indices.push(next, current + 1, next + 1);
                    }
                }
            }
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();

        const material = new THREE.MeshLambertMaterial({
            color: this._getBlendedTerrainColor(),
            flatShading: true
        });
        this.terrainMaterials.push(material);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.receiveShadow = true;
        mesh.castShadow = false;
        return mesh;
    }

    /**
     * Create low-resolution mountain terrain (low-poly aesthetic)
     * Starts after the roadside strip
     *
     * Uses terrain boundary cache to ensure adjacent chunks share exact vertex positions
     * at their boundaries, eliminating visible seams.
     */
    createMountainTerrain(roadData, side, noiseOffset = 0) {
        const geometry = new THREE.BufferGeometry();
        const vertices = [];
        const indices = [];

        const edgeDirection = side === 'left' ? -1 : 1;
        const stripWidth = this._getRoadsideStripWidth(side);
        const stripOverlap = 0.15; // Must match the overlap in createRoadsideStrip
        const mountainWidth = this.terrainWidth - stripWidth;
        const segmentsOut = this.terrainSegmentsOut;

        // Helper to create cache key from Z value
        const roundZ = (z) => Math.round(z * 1000) / 1000;
        const getCacheKey = (z) => `${roundZ(z)}_${side}`;

        // Get the first road point's Z for cache lookup (this is the START of this chunk)
        const firstZ = roadData[0].z;
        const firstCacheKey = getCacheKey(firstZ);
        const cachedFirstRow = this.terrainBoundaryCache.get(firstCacheKey);

        // Get the last road point's Z for caching (this will be the START of next chunk)
        const lastZ = roadData[roadData.length - 1].z;
        const lastCacheKey = getCacheKey(lastZ);

        // Pre-calculate curvature scaling to prevent terrain overlap on inside of curves
        // When road curves, the inside terrain rows converge and can overlap
        const curvatureScales = [];
        for (let i = 0; i < roadData.length; i++) {
            let scale = 1.0;

            // Check angle change between adjacent road segments
            if (i > 0 && i < roadData.length - 1) {
                const prevPos = roadData[i - 1].position;
                const currPos = roadData[i].position;
                const nextPos = roadData[i + 1].position;

                // Vector from prev to curr and curr to next (road direction)
                const dx1 = currPos.x - prevPos.x;
                const dz1 = currPos.z - prevPos.z;
                const dx2 = nextPos.x - currPos.x;
                const dz2 = nextPos.z - currPos.z;

                // Cross product to determine turn direction
                // In our coordinate system (negative Z = forward):
                // Positive cross = turning right (clockwise when viewed from above)
                // Negative cross = turning left (counter-clockwise when viewed from above)
                const cross = dx1 * dz2 - dz1 * dx2;

                // Scale down terrain on inside of curve only
                // Right turn (cross > 0): right side is inside, left side is outside
                // Left turn (cross < 0): left side is inside, right side is outside
                // edgeDirection: -1 = left side, +1 = right side
                const isInsideCurve = (edgeDirection > 0 && cross > 0) || (edgeDirection < 0 && cross < 0);

                if (isInsideCurve) {
                    // Scale based on curvature intensity
                    // Use absolute cross product magnitude as curvature measure
                    // Cross product magnitude depends on segment length and turn angle
                    const curvatureIntensity = Math.abs(cross);

                    // More aggressive scaling to prevent overlap on sharp curves
                    // Scale from 1.0 (straight) down to 0.15 (very sharp curve)
                    const shrinkFactor = Math.min(curvatureIntensity * 0.001, 0.85);
                    scale = Math.max(0.15, 1 - shrinkFactor);
                }
            }
            curvatureScales.push(scale);
        }

        for (let i = 0; i < roadData.length; i++) {
            const { position, right, z } = roadData[i];
            const isFirstRow = (i === 0);
            const isLastRow = (i === roadData.length - 1);

            // Get curvature-based scale for this row
            const curveScale = curvatureScales[i];

            // Start slightly INSIDE the strip to ensure overlap (hides any gaps from different sampling)
            // Mountains start a few units before strip ends to guarantee connection
            const mountainOverlapIntoStrip = this.mountainOverlapIntoStrip;
            const stripEnd = this._tempVec1.copy(position).addScaledVector(right, edgeDirection * (this.roadWidth / 2 - stripOverlap + stripWidth - mountainOverlapIntoStrip));

            const noiseZ = noiseOffset + z;
            const distanceNoise = this.noise.noise(noiseZ * this.terrainDistanceNoiseScale, 500 + (side === 'left' ? 0 : 100));
            const normalizedNoise = (distanceNoise + 1) / 2;

            // Variable minimum flat zone (uses terrainFlatZoneMin config)
            const flatZone = this.terrainFlatZoneMin + normalizedNoise * (this.terrainFlatZoneMax - this.terrainFlatZoneMin);
            const blendRange = this.terrainBlendRange;

            // Height at strip edge (for blending)
            const stripEdgeHeight = position.y;

            // Array to store this row's vertices (for caching if last row)
            const rowVertices = [];

            for (let j = 0; j <= segmentsOut; j++) {
                const t = j / segmentsOut;
                // Apply curvature scaling to prevent overlap on inside of curves
                // Scale increases with distance from road (outer terrain shrinks more on tight curves)
                const effectiveScale = 1 - (1 - curveScale) * t; // Gradual scaling from 1 at road to curveScale at outer edge
                const distance = t * mountainWidth * effectiveScale;
                const totalDistance = stripWidth + distance;

                const vertexPos = this._tempVec2.copy(stripEnd).addScaledVector(right, edgeDirection * distance);

                let height;
                const normalizedDist = distance / mountainWidth;

                // If this is the first row and we have cached data, use exact cached values
                if (isFirstRow && cachedFirstRow && cachedFirstRow.length > j) {
                    const cached = cachedFirstRow[j];
                    vertices.push(cached.x, cached.y, cached.z);
                    rowVertices.push({ x: cached.x, y: cached.y, z: cached.z });
                } else {
                    // Calculate height normally
                    if (normalizedDist < flatZone) {
                        height = stripEdgeHeight;
                    } else {
                        let terrainHeight = this.generateTerrainHeightWithOffset(vertexPos.x, vertexPos.z, noiseOffset, totalDistance, side);

                        if (normalizedDist < flatZone + blendRange) {
                            const blendFactor = (normalizedDist - flatZone) / blendRange;
                            const smoothBlend = blendFactor * blendFactor * (3 - 2 * blendFactor);
                            height = stripEdgeHeight * (1 - smoothBlend) + terrainHeight * smoothBlend;
                        } else {
                            height = terrainHeight;
                        }
                    }

                    vertices.push(vertexPos.x, height, vertexPos.z);
                    rowVertices.push({ x: vertexPos.x, y: height, z: vertexPos.z });
                }

                if (i < roadData.length - 1 && j < segmentsOut) {
                    const current = i * (segmentsOut + 1) + j;
                    const next = (i + 1) * (segmentsOut + 1) + j;

                    if (side === 'left') {
                        indices.push(current, next, current + 1);
                        indices.push(next, next + 1, current + 1);
                    } else {
                        indices.push(current, current + 1, next);
                        indices.push(next, current + 1, next + 1);
                    }
                }
            }

            // Cache the last row for the next chunk to use as its first row
            if (isLastRow) {
                this.terrainBoundaryCache.set(lastCacheKey, rowVertices);

                // Clean up old cache entries
                if (this.terrainBoundaryCache.size > 400) {
                    const firstKey = this.terrainBoundaryCache.keys().next().value;
                    this.terrainBoundaryCache.delete(firstKey);
                }
            }
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();

        const material = new THREE.MeshLambertMaterial({
            color: this._getBlendedTerrainColor(),
            flatShading: true
        });
        this.terrainMaterials.push(material);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.receiveShadow = true;
        mesh.castShadow = false;
        return mesh;
    }

    /**
     * Create grid-based terrain that doesn't depend on road direction.
     * Uses fixed world-space X/Z coordinates for uniform, predictable geometry.
     * Terrain is carved out around the road's actual position at each Z.
     *
     * @param {number} zStart - Starting Z coordinate (closer to camera)
     * @param {number} zEnd - Ending Z coordinate (further from camera)
     * @param {string} side - 'left' or 'right' side of the road
     * @param {number} noiseOffset - Offset for noise sampling
     */
    createGridTerrain(zStart, zEnd, side, noiseOffset = 0) {
        const geometry = new THREE.BufferGeometry();
        const vertices = [];
        const indices = [];

        // Configuration - must match createRoadsideStrip for seamless connection
        // cellSizeZ must divide evenly into chunkLength (50) to prevent boundary gaps
        const cellSizeZ = 50;    // 50/50 = 1 segment per chunk, aligns at boundaries
        const cellSizeX = 20;     // Higher resolution perpendicular to road
        const terrainWidth = this.terrainWidth;
        const blendDistance = 120; // Blend road height to terrain height

        // Grid starts where strip ends, with overlap to hide seams
        const stripOverlapIntoGrid = 15;
        const baseRoadClearance = this.roadWidth / 2 + this._getRoadsideStripWidth(side) - stripOverlapIntoGrid;

        // Diagonal skirt depth - extends toward road direction from inner edge to fill gaps during dips
        const skirtDepth = 10;

        const edgeDirection = side === 'left' ? -1 : 1;

        // Calculate grid dimensions
        const segmentsOut = Math.ceil(terrainWidth / cellSizeX);
        const segmentsZ = Math.ceil(Math.abs(zEnd - zStart) / cellSizeZ);

        // Track inner edge vertices for extension and skirt creation
        const innerEdgeVerts = [];

        // Generate vertices - sample road position at each Z for proper curve following
        for (let iz = 0; iz <= segmentsZ; iz++) {
            const z = zStart - iz * cellSizeZ;

            // Get road data at this Z
            const roadData = this.getRoadDataAtZ(z);
            const roadX = roadData ? roadData.position.x : 0;
            const roadY = roadData ? roadData.position.y : 0;

            // Use shared curve clearance calculation
            const curveExtraClearance = this.calculateCurveClearance(z, cellSizeZ);

            const roadClearance = baseRoadClearance + curveExtraClearance;

            for (let ix = 0; ix <= segmentsOut; ix++) {
                const distanceOut = ix * cellSizeX;

                // X position: road center + clearance + distance out
                const x = roadX + edgeDirection * (roadClearance + distanceOut);
                const totalDist = roadClearance + distanceOut;

                // Get natural terrain height (with per-side parameters)
                const terrainHeight = this.generateTerrainHeightWithOffset(x, z, noiseOffset, totalDist, side);

                // Blend height from road to terrain
                let height;
                if (distanceOut < blendDistance) {
                    const blendT = distanceOut / blendDistance;
                    const smoothT = blendT * blendT * (3 - 2 * blendT);
                    height = roadY + (terrainHeight - roadY) * smoothT;
                } else {
                    height = terrainHeight;
                }

                vertices.push(x, height, z);

                // Store inner edge vertex info for skirt
                if (ix === 0) {
                    innerEdgeVerts.push({ x, height, z, iz });
                }

                // Create triangles
                if (iz < segmentsZ && ix < segmentsOut) {
                    const current = iz * (segmentsOut + 1) + ix;
                    const next = (iz + 1) * (segmentsOut + 1) + ix;

                    if (side === 'left') {
                        indices.push(current, next, current + 1);
                        indices.push(next, next + 1, current + 1);
                    } else {
                        indices.push(current, current + 1, next);
                        indices.push(next, current + 1, next + 1);
                    }
                }
            }
        }

        // Add angled skirt vertices (steeper angle toward road)
        // Horizontal offset is 2x vertical drop for ~63 degree angle
        const mainVertCount = vertices.length / 3;
        for (const vert of innerEdgeVerts) {
            const skirtX = vert.x - edgeDirection * skirtDepth * 2; // Move toward road at steeper angle
            vertices.push(skirtX, vert.height - skirtDepth, vert.z);
        }

        // Create skirt triangles connecting inner edge to dropped vertices
        for (let i = 0; i < innerEdgeVerts.length - 1; i++) {
            const topCurrent = innerEdgeVerts[i].iz * (segmentsOut + 1); // Inner edge vertex in main grid
            const topNext = innerEdgeVerts[i + 1].iz * (segmentsOut + 1);
            const bottomCurrent = mainVertCount + i;
            const bottomNext = mainVertCount + i + 1;

            if (side === 'left') {
                // Skirt faces outward (toward road) on left side
                indices.push(topCurrent, bottomCurrent, topNext);
                indices.push(bottomCurrent, bottomNext, topNext);
            } else {
                // Skirt faces outward (toward road) on right side
                indices.push(topCurrent, topNext, bottomCurrent);
                indices.push(bottomCurrent, topNext, bottomNext);
            }
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();

        const material = new THREE.MeshLambertMaterial({
            color: this._getBlendedTerrainColor(),
            flatShading: true
        });
        this.terrainMaterials.push(material);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.receiveShadow = true;
        mesh.castShadow = false;
        return mesh;
    }

    /**
     * Creates a bridge mesh that connects the high-res strip outer edge to the low-res mountain inner edge.
     * This fills gaps that occur on hills where the two meshes sample the road at different resolutions.
     */
    createBridgeMesh(highResRoadData, lowResRoadData, side, noiseOffset = 0) {
        const geometry = new THREE.BufferGeometry();
        const vertices = [];
        const indices = [];

        const edgeDirection = side === 'left' ? -1 : 1;
        const stripWidth = this._getRoadsideStripWidth(side);
        const stripOverlap = this.roadsideStripOverlap;
        const mountainOverlap = this.mountainOverlapIntoStrip;

        // Calculate positions for strip outer edge and mountain inner edge
        const stripOuterOffset = this.roadWidth / 2 - stripOverlap + stripWidth;
        const mountainInnerOffset = this.roadWidth / 2 - stripOverlap + stripWidth - mountainOverlap;

        // Build arrays of edge vertices
        // Strip outer edge: high-res, at road height
        const stripEdgeVerts = [];
        for (let i = 0; i < highResRoadData.length; i++) {
            const { position, right, z } = highResRoadData[i];
            const edgePos = this._tempVec1.copy(position).addScaledVector(right, edgeDirection * stripOuterOffset);
            stripEdgeVerts.push({
                x: edgePos.x,
                y: position.y, // Strip is at road height
                z: edgePos.z,
                roadZ: z
            });
        }

        // Mountain inner edge: low-res, at road height (matching mountain's inner edge)
        const mountainEdgeVerts = [];
        for (let i = 0; i < lowResRoadData.length; i++) {
            const { position, right, z } = lowResRoadData[i];
            const edgePos = this._tempVec1.copy(position).addScaledVector(right, edgeDirection * mountainInnerOffset);
            mountainEdgeVerts.push({
                x: edgePos.x,
                y: position.y, // Mountain starts at road height
                z: edgePos.z,
                roadZ: z
            });
        }

        // Create vertices: first all strip edge verts, then all mountain edge verts
        const stripVertCount = stripEdgeVerts.length;

        for (const v of stripEdgeVerts) {
            vertices.push(v.x, v.y, v.z);
        }
        for (const v of mountainEdgeVerts) {
            vertices.push(v.x, v.y, v.z);
        }

        // Triangulate between the two edges
        // For each low-res segment, find the corresponding high-res vertices and create triangles
        for (let lowIdx = 0; lowIdx < lowResRoadData.length - 1; lowIdx++) {
            const lowZ0 = mountainEdgeVerts[lowIdx].roadZ;
            const lowZ1 = mountainEdgeVerts[lowIdx + 1].roadZ;

            // Find high-res vertices that fall within this low-res segment
            const highIndicesInSegment = [];
            for (let highIdx = 0; highIdx < stripEdgeVerts.length; highIdx++) {
                const highZ = stripEdgeVerts[highIdx].roadZ;
                if (highZ <= lowZ0 && highZ >= lowZ1) {
                    highIndicesInSegment.push(highIdx);
                }
            }

            if (highIndicesInSegment.length === 0) continue;

            const mountainIdx0 = stripVertCount + lowIdx;
            const mountainIdx1 = stripVertCount + lowIdx + 1;

            // Create triangles connecting high-res strip to low-res mountain
            for (let j = 0; j < highIndicesInSegment.length; j++) {
                const highIdx = highIndicesInSegment[j];

                if (j === 0) {
                    // First triangle: connects to mountain start
                    if (side === 'left') {
                        indices.push(mountainIdx0, highIdx, mountainIdx1);
                    } else {
                        indices.push(mountainIdx0, mountainIdx1, highIdx);
                    }
                }

                if (j < highIndicesInSegment.length - 1) {
                    // Middle triangles: connect consecutive high-res vertices to mountain end
                    const nextHighIdx = highIndicesInSegment[j + 1];
                    if (side === 'left') {
                        indices.push(highIdx, nextHighIdx, mountainIdx1);
                    } else {
                        indices.push(highIdx, mountainIdx1, nextHighIdx);
                    }
                } else {
                    // Last triangle: connects to mountain end
                    if (side === 'left') {
                        indices.push(highIdx, mountainIdx1, mountainIdx1);
                    } else {
                        // Already connected above
                    }
                }
            }
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();

        const material = new THREE.MeshLambertMaterial({
            color: this._getBlendedTerrainColor(),
            flatShading: true,
            side: THREE.DoubleSide // Ensure visible from both sides
        });
        this.terrainMaterials.push(material);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.receiveShadow = true;
        mesh.castShadow = false;
        return mesh;
    }

    generateTerrainHeight(x, z, distanceFromRoad = 0, side = null) {
        // Use local Z directly for consistent terrain across chunk boundaries
        // Local Z coordinates are absolute within worldGroup, so the same Z always gives the same height
        return this.generateTerrainHeightWithOffset(x, z, 0, distanceFromRoad, side);
    }

    generateTerrainHeightWithOffset(x, z, noiseOffset, distanceFromRoad = 0, side = null) {
        // Generate terrain height using a fixed noise offset
        // noiseOffset should be captured at chunk creation time for consistency
        const noiseZ = noiseOffset + z;

        // Get side-specific config if available
        const sideConfig = side ? this.terrainSides[side] : null;

        // Check if this side should force water level (e.g., ocean)
        if (sideConfig && sideConfig.forceWaterLevel) {
            const waterY = this.effectiveWaterLevel !== undefined ? this.effectiveWaterLevel : this.waterLevel;
            // Add some wave-like variation for ocean effect
            const waveNoise = this.noise.noise(x * 0.01, noiseZ * 0.01) * 3;
            const distanceFromEdge = distanceFromRoad;
            // Gradually lower terrain as it goes further from road
            const depthFactor = Math.min(1.0, distanceFromEdge / 100);
            return waterY - 5 - (depthFactor * 20) + waveNoise + (sideConfig.waterLevelOffset || 0);
        }

        // Use feature-based system if enabled
        // Pass both noiseZ (for terrain noise) and z (for transition blending)
        if (this.terrainFeaturesEnabled) {
            return this.generateFeatureBasedHeight(x, noiseZ, distanceFromRoad, side, z);
        }

        // Fallback to original multi-scale terrain generation with per-side amplitudes
        // Always use _getTerrainParam with Z coordinate for per-row blending during biome transitions
        const amplitudeLarge = this._getTerrainParam('amplitudeLarge', side, z);
        const amplitudeMedium = this._getTerrainParam('amplitudeMedium', side, z);
        const amplitudeSmall = this._getTerrainParam('amplitudeSmall', side, z);
        const amplitudeFine = this._getTerrainParam('amplitudeFine', side, z);
        const heightMultiplier = this._getTerrainParam('heightMultiplier', side, z);

        const scale1 = 0.0005;
        const largeScale = this.noise.noise(x * scale1, noiseZ * scale1) * amplitudeLarge;

        const scale2 = 0.002;
        const mediumScale = this.noise.noise(x * scale2, noiseZ * scale2) * amplitudeMedium;

        const scale3 = 0.008;
        const smallScale = this.noise.noise(x * scale3, noiseZ * scale3) * amplitudeSmall;

        const scale4 = 0.02;
        const fineDetail = this.noise.noise(x * scale4, noiseZ * scale4) * amplitudeFine;

        let height = (largeScale + mediumScale + smallScale + fineDetail) * heightMultiplier;

        // Ensure water areas exist at low points (use effective level if calculated)
        const waterY = this.effectiveWaterLevel !== undefined ? this.effectiveWaterLevel : this.waterLevel;
        if (height < waterY + 3) {
            height = waterY - 2;
        }

        return height;
    }

    /**
     * Generate terrain height using the feature-based system
     * Blends between different terrain feature types based on position
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate (with noise offset already applied)
     * @param {number} distanceFromRoad - Distance from road edge
     * @param {string} side - 'left' or 'right' for per-side parameters
     * @param {number} worldZ - World Z coordinate for transition blending (optional)
     */
    generateFeatureBasedHeight(x, z, distanceFromRoad = 0, side = null, worldZ = null) {
        // Base amplitude from config (use per-side if available)
        // Always use _getTerrainParam with worldZ for per-row blending during biome transitions
        const transitionZ = worldZ !== null ? worldZ : z;
        const baseAmplitude = this._getTerrainParam('amplitudeLarge', side, transitionZ);
        const heightMultiplier = this._getTerrainParam('heightMultiplier', side, transitionZ);
        const amplitudeFine = this._getTerrainParam('amplitudeFine', side, transitionZ);

        // Get distance-based height multiplier (creates layered depth)
        const distanceMultiplier = this.getDistanceHeightMultiplier(distanceFromRoad);

        // Get feature blend for this position
        const featureBlend = this.getTerrainFeatureBlend(x, z);

        // Calculate primary feature height
        const primaryHeight = this.calculateFeatureHeight(
            featureBlend.primary,
            x, z,
            baseAmplitude
        );

        // Calculate secondary feature height for blending
        const secondaryHeight = this.calculateFeatureHeight(
            featureBlend.secondary,
            x, z,
            baseAmplitude
        );

        // Blend features
        let height = primaryHeight * featureBlend.primaryWeight +
                    secondaryHeight * (1.0 - featureBlend.primaryWeight);

        // Apply distance multiplier for layered effect
        height *= distanceMultiplier;

        // Apply height multiplier (per-side or global)
        height *= heightMultiplier;

        // Add fine surface detail (consistent across all features)
        const fineDetail = this.noise.noise(x * 0.02, z * 0.02) * amplitudeFine;
        height += fineDetail * distanceMultiplier;

        // Ensure water areas exist at low points (use effective level if calculated)
        const waterY = this.effectiveWaterLevel !== undefined ? this.effectiveWaterLevel : this.waterLevel;
        if (height < waterY + 3) {
            height = waterY - 2;
        }

        return height;
    }

    getBlendedTerrainHeight(x, z, distanceFromRoadEdge, side = 'right') {
        // Calculate terrain height matching the actual terrain geometry structure:
        // 1. Roadside strip (0 to roadsideStripWidth) - uses noise-based flat zone + blend
        // 2. Grid terrain (beyond strip) - uses fixed blendDistance from strip edge

        // Get road data
        const roadData = this.getRoadDataAtZ(z);
        if (!roadData) return this.generateTerrainHeight(x, z, distanceFromRoadEdge, side);

        const roadHeight = roadData.position.y;

        // Grid terrain configuration - must match createGridTerrain
        const stripOverlapIntoGrid = 15;
        const gridBlendDistance = 120;

        // Where grid terrain starts (from road edge)
        const stripWidth = this._getRoadsideStripWidth(side);
        const gridStartDistance = stripWidth - stripOverlapIntoGrid;

        let height;

        if (distanceFromRoadEdge < gridStartDistance) {
            // In roadside strip zone - use noise-based flat zone logic
            const t = distanceFromRoadEdge / stripWidth;

            // Use same noise-based flat zone calculation as createRoadsideStrip
            const distanceNoise = this.noise.noise(z * this.terrainDistanceNoiseScale, 500 + (side === 'left' ? 0 : 100));
            const normalizedNoise = (distanceNoise + 1) / 2;
            const flatZone = this.terrainFlatZoneMin + normalizedNoise * (this.terrainFlatZoneMax - this.terrainFlatZoneMin);
            const blendRange = this.terrainBlendRange;

            const terrainHeight = this.generateTerrainHeight(x, z, distanceFromRoadEdge, side);

            if (t < flatZone) {
                height = roadHeight;
            } else if (t < flatZone + blendRange) {
                const blendFactor = (t - flatZone) / blendRange;
                const smoothBlend = blendFactor * blendFactor * (3 - 2 * blendFactor);
                height = roadHeight * (1 - smoothBlend) + terrainHeight * smoothBlend;
            } else {
                height = terrainHeight;
            }
        } else {
            // In grid terrain zone - use fixed blend distance logic matching createGridTerrain
            const distanceIntoGrid = distanceFromRoadEdge - gridStartDistance;
            const terrainHeight = this.generateTerrainHeight(x, z, distanceFromRoadEdge, side);

            if (distanceIntoGrid < gridBlendDistance) {
                const blendT = distanceIntoGrid / gridBlendDistance;
                const smoothT = blendT * blendT * (3 - 2 * blendT);
                height = roadHeight + (terrainHeight - roadHeight) * smoothT;
            } else {
                height = terrainHeight;
            }
        }

        return height;
    }

    /**
     * Get terrain surface height at a point using raycast
     * More accurate than getBlendedTerrainHeight since it finds actual mesh surface
     * @param {number} x - Local X coordinate (within worldTranslator group)
     * @param {number} z - Local Z coordinate (within worldTranslator group)
     * @returns {number|null} - Y height at surface, or null if no terrain found
     */
    getTerrainHeightAtPoint(x, z) {
        // The x,z coordinates are in worldTranslator local space
        // Convert to world space by adding the worldGroup offset
        const groupOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;
        const worldZ = z + groupOffset;

        // Cast ray from high above straight down in world space
        this._rayOrigin.set(x, 500, worldZ);
        this._terrainRaycaster.set(this._rayOrigin, this._rayDirection);

        // Reuse array to avoid garbage collection
        this._terrainMeshCache.length = 0;

        // Only test chunks that could contain this Z position
        // Each chunk spans chunkLength units in Z
        for (const chunk of this.terrainChunks) {
            // Quick bounds check - skip chunks that can't possibly contain this Z
            // Chunks have roadData with Z range info
            if (chunk.roadData && chunk.roadData.length > 0) {
                const chunkZStart = chunk.roadData[0].z;
                const chunkZEnd = chunk.roadData[chunk.roadData.length - 1].z;
                // Add margin for terrain width extending beyond road
                const margin = this.terrainWidth;
                if (z > chunkZStart + margin || z < chunkZEnd - margin) {
                    continue; // Skip this chunk
                }
            }

            if (chunk.leftStrip) this._terrainMeshCache.push(chunk.leftStrip);
            if (chunk.rightStrip) this._terrainMeshCache.push(chunk.rightStrip);
            if (chunk.left) this._terrainMeshCache.push(chunk.left);
            if (chunk.right) this._terrainMeshCache.push(chunk.right);
        }

        if (this._terrainMeshCache.length === 0) return null;

        // intersectObjects returns points in world space
        const intersects = this._terrainRaycaster.intersectObjects(this._terrainMeshCache, false);

        if (intersects.length > 0) {
            return intersects[0].point.y;
        }

        return null;
    }

    updateChunks() {
        if (!this.worldTranslator || this.roadChunks.length === 0) return;

        const maxChunksPerFrame = 5;
        let chunksProcessed = 0;

        // Garbage collect old chunks that are behind the camera
        while (chunksProcessed < maxChunksPerFrame && this.roadChunks.length > 0) {
            const oldestChunk = this.roadChunks[0];

            // Check if chunk is far enough behind camera to remove
            if (!this.worldTranslator.isPastCamera(oldestChunk.baseZ, this.chunkLength * 10)) {
                break; // Oldest chunk is still visible, stop
            }

            // Remove and dispose oldest road chunk
            const oldRoadChunk = this.roadChunks.shift();
            this.worldTranslator.remove(oldRoadChunk.mesh);
            oldRoadChunk.mesh.geometry.dispose();
            oldRoadChunk.markings.forEach(m => {
                this.worldTranslator.remove(m);
                m.geometry.dispose();
                m.material.dispose();
            });

            // Clean up centerline cache entries behind this chunk
            this.cleanupCenterlineCache(oldRoadChunk.baseZ);

            // Remove and dispose oldest terrain chunk (including strips and bridges)
            const oldTerrainChunk = this.terrainChunks.shift();
            // Each chunk creates 4 materials: leftStrip, rightStrip, leftMountain, rightMountain
            this.terrainMaterials.shift();
            this.terrainMaterials.shift();
            this.terrainMaterials.shift();
            this.terrainMaterials.shift();

            // Remove mountain terrain
            this.worldTranslator.remove(oldTerrainChunk.left);
            this.worldTranslator.remove(oldTerrainChunk.right);
            oldTerrainChunk.left.geometry.dispose();
            oldTerrainChunk.left.material.dispose();
            oldTerrainChunk.right.geometry.dispose();
            oldTerrainChunk.right.material.dispose();

            // Remove roadside strips (if they exist)
            if (oldTerrainChunk.leftStrip) {
                this.worldTranslator.remove(oldTerrainChunk.leftStrip);
                oldTerrainChunk.leftStrip.geometry.dispose();
                oldTerrainChunk.leftStrip.material.dispose();
            }
            if (oldTerrainChunk.rightStrip) {
                this.worldTranslator.remove(oldTerrainChunk.rightStrip);
                oldTerrainChunk.rightStrip.geometry.dispose();
                oldTerrainChunk.rightStrip.material.dispose();
            }

            // Remove bridge meshes (if they exist)
            if (oldTerrainChunk.leftBridge) {
                this.worldTranslator.remove(oldTerrainChunk.leftBridge);
                oldTerrainChunk.leftBridge.geometry.dispose();
                oldTerrainChunk.leftBridge.material.dispose();
            }
            if (oldTerrainChunk.rightBridge) {
                this.worldTranslator.remove(oldTerrainChunk.rightBridge);
                oldTerrainChunk.rightBridge.geometry.dispose();
                oldTerrainChunk.rightBridge.material.dispose();
            }

            // Generate a new chunk ahead
            const newestChunk = this.roadChunks[this.roadChunks.length - 1];
            const newIndex = newestChunk.index + 1;
            // New chunk starts where the furthest chunk ended
            const newZStart = this.furthestGeneratedZ;

            // Extend road path to cover the new chunk plus lookahead for CatmullRom spline
            // CatmullRom needs 4 control points (p0, p1, p2, p3). Without enough lookahead,
            // p3 gets extrapolated linearly, causing kinks when real points are added later.
            // Extend 3 extra segments to ensure p3 is always a real point.
            const lookahead = this.pathSegmentLength * 3;
            const targetZ = newZStart - this.chunkLength - lookahead;
            while (this.pathPoints.length > 0 &&
                   this.pathPoints[this.pathPoints.length - 1].z > targetZ) {
                this.extendRoadPath();
            }

            // Create new chunks
            this.createRoadChunk(newIndex, newZStart);
            this.createTerrainChunk(newIndex, newZStart);

            // Update furthest generated position (end of new chunk)
            this.furthestGeneratedZ = newZStart - this.chunkLength;

            chunksProcessed++;
        }
    }

    update(deltaTime) {
        this.updateChunks();
        // Use new spawner for roadside objects
        this.roadsideSpawner.update();

        // Water plane is now in worldGroup and moves with the world
        // We just need to keep it centered under the camera's X position (lateral offset)
        if (this.waterPlane) {
            this.waterPlane.position.x = this.camera.position.x;
            // Z position is handled by worldGroup translation
        }
    }

    setPalette(palette, timeIndex = 0) {
        // Delegate to palette manager
        this.palette.setPalette(palette, timeIndex);
    }

    /**
     * Get terrain colors for a specific time of day
     * Used by ColorCycleManager to determine terrain material colors
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of terrain color hex values
     */
    getTerrainColors(timeIndex) {
        if (this.biomeColors && this.biomeColors[timeIndex]) {
            const colors = this.biomeColors[timeIndex].terrainColors || [];
            // Debug: log occasionally (not every frame)
            if (Math.random() < 0.001) {
                console.log(`[BiomeRenderer.getTerrainColors] biome=${this.biomeName}, timeIndex=${timeIndex}, colors:`, colors.map(c => c.toString(16)));
            }
            return colors;
        }
        console.log(`[BiomeRenderer.getTerrainColors] WARNING: No biomeColors for timeIndex=${timeIndex}, biome=${this.biomeName}`);
        return [];
    }

    /**
     * Get rock colors for a specific time of day
     * Used by ColorCycleManager
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of rock color hex values
     */
    getRockColors(timeIndex) {
        if (this.biomeColors && this.biomeColors[timeIndex]) {
            return this.biomeColors[timeIndex].rockColors || [];
        }
        return [];
    }

    /**
     * Get shrub colors for a specific time of day
     * Used by ColorCycleManager
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of shrub color hex values
     */
    getShrubColors(timeIndex) {
        if (this.biomeColors && this.biomeColors[timeIndex]) {
            return this.biomeColors[timeIndex].shrubColors || [];
        }
        return [];
    }

    /**
     * Get rock colors for the current time of day
     * Delegates to BiomePalette
     */
    getCurrentRockColors() {
        return this.palette.getCurrentRockColors();
    }

    /**
     * Get shrub colors for the current time of day
     * Delegates to BiomePalette
     */
    getCurrentShrubColors() {
        return this.palette.getCurrentShrubColors();
    }

    /**
     * Get colors for a specific category (for RoadsideSpawner)
     * Maps category names to biome palette colors
     * @param {string} category - Color category (e.g., 'rock', 'vegetation')
     * @returns {number[]} Array of color hex values
     */
    getColorsByCategory(category) {
        switch (category) {
            case 'rock':
                return this.palette.getCurrentRockColors();
            case 'vegetation':
            case 'shrub':
            case 'tree':
            case 'grass':
                return this.palette.getCurrentShrubColors();
            default:
                // Fallback to vegetation colors for unknown categories
                return this.palette.getCurrentShrubColors();
        }
    }

    updateRoadsideObjectColors(rockColor, shrubColor, vegetationEmissiveIntensity = 0.3) {
        // Convert to Map for RoadsideSpawner
        const colorsByCategory = new Map([
            ['rock', rockColor],
            ['vegetation', shrubColor],
            ['shrub', shrubColor],
            ['tree', shrubColor],
            ['grass', shrubColor]
        ]);

        // Delegate to roadside spawner with new Map-based API
        this.roadsideSpawner.updateColors(colorsByCategory, vegetationEmissiveIntensity);

        // Update palette's cached values
        this.palette.updateCachedColors(rockColor, shrubColor, vegetationEmissiveIntensity);
    }

    /**
     * Get debug stats for long-running diagnostics
     */
    getDebugStats() {
        const oldestRoadChunk = this.roadChunks[0];
        const newestRoadChunk = this.roadChunks[this.roadChunks.length - 1];

        // Calculate expected chunk count
        const expectedChunkCount = 100; // Initial chunk count from initialize()

        // Check path point Z range
        const pathMinZ = this.pathPoints?.length > 0 ? this.pathPoints[this.pathPoints.length - 1].z : null;
        const pathMaxZ = this.pathPoints?.length > 0 ? this.pathPoints[0].z : null;

        // Get worldTranslator stats
        const groupOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;

        // Get pool stats if available
        const poolStats = this.poolManager ? this.poolManager.getStats() : null;

        return {
            roadChunkCount: this.roadChunks.length,
            terrainChunkCount: this.terrainChunks.length,
            roadsideObjectCount: this.roadsideSpawner.getObjectCount(),
            pathPointCount: this.pathPoints ? this.pathPoints.length : 0,
            pathZRange: pathMinZ !== null ? `${pathMaxZ.toFixed(0)} to ${pathMinZ.toFixed(0)}` : null,
            oldestChunkIndex: oldestRoadChunk ? oldestRoadChunk.index : null,
            newestChunkIndex: newestRoadChunk ? newestRoadChunk.index : null,
            lastRoadsideObjectZ: this.roadsideSpawner.lastSpawnZ,
            groupOffset: groupOffset,
            furthestGeneratedZ: this.furthestGeneratedZ,
            chunkCountMismatch: this.roadChunks.length !== this.terrainChunks.length,
            chunkDeficit: expectedChunkCount - this.roadChunks.length,
            poolStats: poolStats
        };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // TERRAIN FEATURE SYSTEM - Advanced noise-based terrain generation
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Normalize feature weights so they sum to 1.0
     * Called during construction
     */
    _normalizeFeatureWeights() {
        const weights = this.terrainFeatureWeights;
        const total = Object.values(weights).reduce((sum, w) => sum + w, 0);
        if (total > 0) {
            this._normalizedFeatureWeights = {};
            for (const key in weights) {
                this._normalizedFeatureWeights[key] = weights[key] / total;
            }
        } else {
            // Fallback to equal weights
            const keys = Object.keys(weights);
            this._normalizedFeatureWeights = {};
            keys.forEach(key => {
                this._normalizedFeatureWeights[key] = 1.0 / keys.length;
            });
        }

        // Build cumulative distribution for feature selection
        this._featureCDF = [];
        let cumulative = 0;
        for (const key in this._normalizedFeatureWeights) {
            cumulative += this._normalizedFeatureWeights[key];
            this._featureCDF.push({ feature: key, threshold: cumulative });
        }
    }

    /**
     * Parse per-side terrain configuration
     * Returns null if no side-specific config, otherwise returns override settings
     * @param {Object} sideConfig - Configuration object for one side
     * @returns {Object|null} Parsed side config or null
     */
    _parseTerrainSideConfig(sideConfig) {
        if (!sideConfig) return null;

        return {
            // Height/amplitude overrides
            heightMultiplier: sideConfig.heightMultiplier,
            amplitudeLarge: sideConfig.amplitudeLarge,
            amplitudeMedium: sideConfig.amplitudeMedium,
            amplitudeSmall: sideConfig.amplitudeSmall,
            amplitudeFine: sideConfig.amplitudeFine,
            // Water/ocean behavior
            forceWaterLevel: sideConfig.forceWaterLevel || false,
            waterLevelOffset: sideConfig.waterLevelOffset || 0,
            // Feature weight overrides
            featureWeights: sideConfig.featureWeights || null
        };
    }

    /**
     * Get roadside strip width for a specific side, with fallback to base value.
     * @param {string} side - 'left' or 'right'
     * @returns {number} The strip width for the specified side
     */
    _getRoadsideStripWidth(side) {
        if (side === 'left' && this.roadsideStripWidthLeft !== undefined) {
            return this.roadsideStripWidthLeft;
        }
        if (side === 'right' && this.roadsideStripWidthRight !== undefined) {
            return this.roadsideStripWidthRight;
        }
        return this.roadsideStripWidth;
    }

    /**
     * Get terrain parameter for a specific side, with fallback to base value.
     * During biome transitions, interpolates based on Z position for smooth blending.
     *
     * @param {string} param - Parameter name (e.g., 'amplitudeLarge')
     * @param {string} side - 'left' or 'right'
     * @param {number} z - Optional Z coordinate for per-row interpolation during transitions
     * @returns {number} The parameter value (possibly interpolated)
     */
    _getTerrainParam(param, side, z = null) {
        const sideConfig = this.terrainSides[side];
        if (sideConfig && sideConfig[param] !== undefined) {
            // Per-side config takes precedence, but still needs transition blending
            const currentValue = sideConfig[param];
            return this._applyTransitionBlend(param, currentValue, z);
        }

        // Get current value based on param name
        let currentValue;
        switch (param) {
            case 'heightMultiplier': currentValue = this.terrainHeightMultiplier; break;
            case 'amplitudeLarge': currentValue = this.terrainAmplitudeLarge; break;
            case 'amplitudeMedium': currentValue = this.terrainAmplitudeMedium; break;
            case 'amplitudeSmall': currentValue = this.terrainAmplitudeSmall; break;
            case 'amplitudeFine': currentValue = this.terrainAmplitudeFine; break;
            default: currentValue = 1.0;
        }

        return this._applyTransitionBlend(param, currentValue, z);
    }

    /**
     * Apply transition blending to a terrain parameter based on Z position.
     * During biome transitions, interpolates from previous biome's value to current.
     *
     * Simple approach: lerp parameters smoothly based on Z distance from transition start.
     * At Z=startZ, t=0 (100% old params). As Z decreases, t increases toward 1 (new params).
     * This creates one continuous surface with no seams.
     *
     * @param {string} param - Parameter name
     * @param {number} currentValue - The new biome's parameter value
     * @param {number} z - Z coordinate for interpolation
     * @returns {number} Blended value
     */
    _applyTransitionBlend(param, currentValue, z) {
        // If no Z provided, return current value (can't calculate blend)
        if (z === null) {
            return currentValue;
        }

        // Check if transition is active OR if we're still within transition distance
        // (transition.active may be false but we still need to blend based on Z)
        if (!this.biomeTransition.previous) {
            return currentValue;
        }

        // Debug: log first blend after transition starts
        if (param === 'amplitudeLarge' && !this._blendLoggedOnce) {
            this._blendLoggedOnce = true;
            const startZ = this.biomeTransition.startZ;
            const traveled = startZ - z;
            const t = Math.max(0, Math.min(1, traveled / this.biomeTransition.distanceZ));
            console.log(`[Blend FIRST CALL] z=${z.toFixed(0)}, startZ=${startZ.toFixed(0)}, traveled=${traveled.toFixed(0)}, t=${t.toFixed(3)}`);
            console.log(`  prev=${this.biomeTransition.previous.terrainAmplitudeLarge}, curr=${currentValue}`);
        }

        const prev = this.biomeTransition.previous;
        let previousValue;

        // Get previous biome's value for this param
        switch (param) {
            case 'heightMultiplier': previousValue = prev.terrainHeightMultiplier; break;
            case 'amplitudeLarge': previousValue = prev.terrainAmplitudeLarge; break;
            case 'amplitudeMedium': previousValue = prev.terrainAmplitudeMedium; break;
            case 'amplitudeSmall': previousValue = prev.terrainAmplitudeSmall; break;
            case 'amplitudeFine': previousValue = prev.terrainAmplitudeFine; break;
            default: previousValue = currentValue;
        }

        // If previous value is same as current, no blending needed
        if (previousValue === currentValue) {
            return currentValue;
        }

        // Calculate interpolation factor based on Z distance from transition start
        // Z goes negative as we travel forward
        const startZ = this.biomeTransition.startZ;
        const distanceZ = this.biomeTransition.distanceZ;

        // Distance traveled since transition started (Z decreases as we go forward)
        const traveled = startZ - z;

        // Simple lerp: t=0 at start, t=1 at distanceZ
        // Clamp to [0, 1] - before start uses old params, after distanceZ uses new params
        let t = Math.max(0, Math.min(1, traveled / distanceZ));

        // Apply smooth easing for gradual transition
        const smoothT = t * t * (3 - 2 * t);

        return previousValue + (currentValue - previousValue) * smoothT;
    }

    /**
     * Ridged noise - creates sharp mountain ridges and peaks
     */
    ridgedNoise(x, z, scale) {
        const n = this.noise.noise(x * scale, z * scale);
        return 1.0 - Math.abs(n); // Invert absolute value for ridges
    }

    /**
     * Ridged multifractal - multiple octaves of ridged noise
     */
    ridgedMultifractal(x, z, scale, octaves = 4, lacunarity = 2.0, gain = 0.5) {
        let sum = 0;
        let amplitude = 1.0;
        let frequency = scale;
        let weight = 1.0;

        for (let i = 0; i < octaves; i++) {
            const n = this.ridgedNoise(x, z, frequency);
            // Weight successive octaves by previous
            const signal = n * weight;
            sum += signal * amplitude;
            // Update weight for next octave
            weight = Math.min(1.0, Math.max(0.0, signal * 2.0));
            amplitude *= gain;
            frequency *= lacunarity;
        }

        return sum;
    }

    /**
     * Terraced noise - creates plateau/mesa effects with flat tops
     */
    terracedNoise(x, z, scale, levels = 5) {
        const n = (this.noise.noise(x * scale, z * scale) + 1) / 2; // 0 to 1
        return Math.floor(n * levels) / (levels - 1);
    }

    /**
     * Billowed noise - creates soft, rolling dunes
     */
    billowedNoise(x, z, scale) {
        return Math.abs(this.noise.noise(x * scale, z * scale));
    }

    /**
     * Domain-warped noise - creates organic, twisted formations
     */
    warpedNoise(x, z, scale, warpAmount = 100) {
        const warpX = this.noise.noise(x * scale * 0.5, z * scale * 0.5) * warpAmount;
        const warpZ = this.noise.noise(x * scale * 0.5 + 100, z * scale * 0.5 + 100) * warpAmount;
        return this.noise.noise((x + warpX) * scale, (z + warpZ) * scale);
    }

    /**
     * Get the dominant terrain feature type at a position
     * Returns blend weights for smooth transitions
     */
    getTerrainFeatureBlend(x, z) {
        // Use very low frequency noise to determine feature regions
        const featureNoise1 = this.noise.noise(x * this.terrainFeatureScale, z * this.terrainFeatureScale);
        const featureNoise2 = this.noise.noise(x * this.terrainFeatureScale * 1.7 + 500, z * this.terrainFeatureScale * 1.7 + 500);

        // Combine for more variation
        const combinedNoise = (featureNoise1 + featureNoise2 * 0.5) / 1.5;
        const normalizedNoise = (combinedNoise + 1) / 2; // 0 to 1

        // Find primary and secondary features based on noise value
        let primaryFeature = 'dunes';
        let secondaryFeature = 'dunes';
        let primaryWeight = 1.0;

        for (let i = 0; i < this._featureCDF.length; i++) {
            if (normalizedNoise <= this._featureCDF[i].threshold) {
                primaryFeature = this._featureCDF[i].feature;

                // Calculate blend with adjacent feature
                const prevThreshold = i > 0 ? this._featureCDF[i - 1].threshold : 0;
                const range = this._featureCDF[i].threshold - prevThreshold;
                const posInRange = (normalizedNoise - prevThreshold) / range;

                // Blend at edges of feature region
                if (posInRange < this.terrainFeatureBlend) {
                    // Blend with previous feature
                    secondaryFeature = i > 0 ? this._featureCDF[i - 1].feature : this._featureCDF[this._featureCDF.length - 1].feature;
                    primaryWeight = 0.5 + (posInRange / this.terrainFeatureBlend) * 0.5;
                } else if (posInRange > 1.0 - this.terrainFeatureBlend) {
                    // Blend with next feature
                    secondaryFeature = i < this._featureCDF.length - 1 ? this._featureCDF[i + 1].feature : this._featureCDF[0].feature;
                    primaryWeight = 0.5 + ((1.0 - posInRange) / this.terrainFeatureBlend) * 0.5;
                }

                break;
            }
        }

        return {
            primary: primaryFeature,
            secondary: secondaryFeature,
            primaryWeight: primaryWeight
        };
    }

    /**
     * Calculate height for dunes/rolling hills feature
     */
    calculateDunesHeight(x, z, amplitude) {
        // Soft, rolling terrain using billowed noise
        const large = this.billowedNoise(x, z, 0.0004) * amplitude * 0.6;
        const medium = this.billowedNoise(x, z, 0.0015) * amplitude * 0.3;
        const small = this.noise.noise(x * 0.006, z * 0.006) * amplitude * 0.1;
        return large + medium + small;
    }

    /**
     * Calculate height for mountain peaks feature
     */
    calculatePeaksHeight(x, z, amplitude) {
        // Sharp ridges using ridged multifractal
        const ridges = this.ridgedMultifractal(x, z, 0.0003, 4, 2.2, 0.6) * amplitude * 0.8;
        const variation = this.warpedNoise(x, z, 0.001, 80) * amplitude * 0.2;
        return ridges + variation;
    }

    /**
     * Calculate height for plateau/mesa feature
     */
    calculatePlateauHeight(x, z, amplitude) {
        // Terraced with steep edges
        const terraced = this.terracedNoise(x, z, 0.0003, 4) * amplitude * 0.7;
        // Add some edge detail
        const edgeDetail = this.ridgedNoise(x, z, 0.002) * amplitude * 0.2;
        // Small surface detail
        const surface = this.noise.noise(x * 0.01, z * 0.01) * amplitude * 0.1;
        return terraced + edgeDetail * (1.0 - this.terracedNoise(x, z, 0.0003, 4)) + surface;
    }

    /**
     * Calculate height for spire/needle formations
     */
    calculateSpireHeight(x, z, amplitude) {
        // Very sharp peaks using high-power ridged noise
        const spireBase = this.ridgedNoise(x, z, 0.0008);
        const spire = Math.pow(spireBase, 3) * amplitude * 1.2; // Cubic for sharp peaks
        // Add some clustering variation
        const cluster = (this.noise.noise(x * 0.0002, z * 0.0002) + 1) / 2;
        return spire * (0.3 + cluster * 0.7);
    }

    /**
     * Calculate height for cliff formations
     */
    calculateCliffHeight(x, z, amplitude) {
        // Asymmetric - steep on one side, gradual on other
        const baseNoise = this.noise.noise(x * 0.0004, z * 0.0004);
        // Create cliff faces using smoothstep-like function
        const cliffFace = baseNoise > 0 ? Math.pow(baseNoise, 0.5) : -Math.pow(-baseNoise, 2);
        const height = cliffFace * amplitude * 0.8;
        // Add erosion detail
        const erosion = this.ridgedNoise(x, z, 0.003) * amplitude * 0.2;
        return height + erosion;
    }

    /**
     * Calculate height for valley/canyon feature
     */
    calculateValleyHeight(x, z, amplitude) {
        // Inverted ridges create valleys
        const valleyBase = this.ridgedMultifractal(x, z, 0.0004, 3, 2.0, 0.5);
        const valley = (1.0 - valleyBase) * amplitude * 0.5 - amplitude * 0.3;
        // Add some floor detail
        const floor = this.noise.noise(x * 0.008, z * 0.008) * amplitude * 0.1;
        return valley + floor;
    }

    /**
     * Get height multiplier based on distance from road
     * Creates layers: smaller features near, larger far
     */
    getDistanceHeightMultiplier(distanceFromRoad) {
        const t = distanceFromRoad / this.terrainWidth;

        // Find which layer we're in
        for (let i = 0; i < this.terrainLayerDistances.length; i++) {
            if (t <= this.terrainLayerDistances[i]) {
                if (i === 0) {
                    // First layer - interpolate from 0 to first layer height
                    const layerT = t / this.terrainLayerDistances[i];
                    return layerT * this.terrainLayerHeights[i];
                } else {
                    // Interpolate between layers
                    const prevDist = this.terrainLayerDistances[i - 1];
                    const layerT = (t - prevDist) / (this.terrainLayerDistances[i] - prevDist);
                    const smoothT = layerT * layerT * (3 - 2 * layerT); // Smoothstep
                    return this.terrainLayerHeights[i - 1] + smoothT * (this.terrainLayerHeights[i] - this.terrainLayerHeights[i - 1]);
                }
            }
        }

        return this.terrainLayerHeights[this.terrainLayerHeights.length - 1];
    }

    /**
     * Calculate height for a specific feature type
     */
    calculateFeatureHeight(feature, x, z, amplitude) {
        switch (feature) {
            case 'dunes':
                return this.calculateDunesHeight(x, z, amplitude);
            case 'peaks':
                return this.calculatePeaksHeight(x, z, amplitude);
            case 'plateaus':
                return this.calculatePlateauHeight(x, z, amplitude);
            case 'spires':
                return this.calculateSpireHeight(x, z, amplitude);
            case 'cliffs':
                return this.calculateCliffHeight(x, z, amplitude);
            case 'valleys':
                return this.calculateValleyHeight(x, z, amplitude);
            default:
                return this.calculateDunesHeight(x, z, amplitude);
        }
    }

    dispose() {
        // Guard against double-dispose
        if (this._disposed) return;
        this._disposed = true;

        // Clean up road chunks
        this.roadChunks.forEach(chunk => {
            if (!chunk?.mesh) return;
            this.worldTranslator.remove(chunk.mesh);
            chunk.mesh.geometry?.dispose();
            chunk.mesh.material?.dispose();
            chunk.markings?.forEach(m => {
                this.worldTranslator.remove(m);
                m.geometry?.dispose();
                m.material?.dispose();
            });
        });

        // Clean up terrain chunks
        this.terrainChunks.forEach(chunk => {
            if (!chunk) return;
            if (chunk.left) {
                this.worldTranslator.remove(chunk.left);
                chunk.left.geometry?.dispose();
                chunk.left.material?.dispose();
            }
            if (chunk.right) {
                this.worldTranslator.remove(chunk.right);
                chunk.right.geometry?.dispose();
                chunk.right.material?.dispose();
            }
            if (chunk.leftStrip) {
                this.worldTranslator.remove(chunk.leftStrip);
                chunk.leftStrip.geometry?.dispose();
                chunk.leftStrip.material?.dispose();
            }
            if (chunk.rightStrip) {
                this.worldTranslator.remove(chunk.rightStrip);
                chunk.rightStrip.geometry?.dispose();
                chunk.rightStrip.material?.dispose();
            }
            if (chunk.leftBridge) {
                this.worldTranslator.remove(chunk.leftBridge);
                chunk.leftBridge.geometry?.dispose();
                chunk.leftBridge.material?.dispose();
            }
            if (chunk.rightBridge) {
                this.worldTranslator.remove(chunk.rightBridge);
                chunk.rightBridge.geometry?.dispose();
                chunk.rightBridge.material?.dispose();
            }
        });

        // Clean up roadside objects via spawner
        if (this.roadsideSpawner) {
            this.roadsideSpawner.dispose();
        }

        // Clean up water plane
        if (this.waterPlane) {
            this.worldTranslator.remove(this.waterPlane);
            this.waterPlane.geometry?.dispose();
            this.waterPlane.material?.dispose();
            this.waterPlane = null;
        }

        this.roadChunks = [];
        this.terrainChunks = [];
        this.terrainMaterials = [];
    }
}
